(() => {
  const focusableSelector = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  let openOverlayElement = null;
  let openOverlayTrigger = null;
  let overlayStateGeneration = 0;
  let cartRefreshGeneration = 0;
  let cartRefreshController = null;
  const inertBackgroundState = new Map();
  const headerStates = new Map();
  const collectionFilterStates = new Map();
  let autoFilterTimer = null;
  let pendingAutoFilterForm = null;
  const predictiveSearchStates = new Map();
  const focusedErrorSummaries = new WeakSet();
  const randomizedProductRails = new WeakSet();
  const randomizedProductGrids = new WeakSet();
  const recommendationStates = new Map();
  let recommendationObserver = null;
  let recommendationObserverInitialized = false;
  const writeWatchdogs = new WeakMap();

  const setHeaderVisibility = (header, hidden) => {
    if (!header) return;
    header.classList.toggle('is-hidden', hidden);
    const bar = header.querySelector('.site-header__bar');
    if (!bar) return;
    bar.inert = hidden;
    bar.toggleAttribute('inert', hidden);
    bar.setAttribute('aria-hidden', String(hidden));
  };

  const elementInRoot = (root, selector) => {
    if (!root) return null;
    if (root.matches?.(selector)) return root;
    return root.querySelector?.(selector) || null;
  };

  const elementsInRoot = (root, selector) => {
    if (!root) return [];
    const elements = [...(root.querySelectorAll?.(selector) || [])];
    if (root.matches?.(selector)) elements.unshift(root);
    return elements;
  };

  const shuffledItems = (items) => {
    const shuffled = [...items];
    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1));
      [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
    }
    return shuffled;
  };

  const initializeRandomProductGrids = (root = document, restore = false) => {
    const explicitSort = new URL(window.location.href).searchParams.get('sort_by');
    elementsInRoot(root, '[data-random-sort]').forEach((select) => {
      const option = select.querySelector('[data-random-sort-option]');
      if (!option) return;
      option.hidden = false;
      if (!explicitSort) select.value = '';
    });
    elementsInRoot(root, '[data-random-product-grid], [data-recommendation-list]').forEach((grid) => {
      if (explicitSort && grid.matches('[data-random-product-grid]')) return;
      if (!restore && randomizedProductGrids.has(grid)) return;
      // Move the rendered cards only: filters, page membership and card controls stay intact.
      if (grid.children.length > 1) grid.replaceChildren(...shuffledItems(grid.children));
      randomizedProductGrids.add(grid);
    });
  };

  const initializeRandomProductRails = (root = document) => {
    elementsInRoot(root, '[data-random-product-rail]').forEach((rail) => {
      if (randomizedProductRails.has(rail)) return;
      const grid = rail.querySelector('[data-product-rail-grid]');
      const count = Number.parseInt(rail.dataset.productsToShow, 10);
      if (!grid || !Number.isInteger(count) || count < 1) return;
      const seenProducts = new Set();
      const candidates = [...rail.querySelectorAll('template[data-random-product]')]
        .slice(0, 50)
        .filter((template) => {
          const productId = template.dataset.randomProduct;
          if (!productId || seenProducts.has(productId) || !template.content?.querySelector('[data-product-card]')) return false;
          seenProducts.add(productId);
          return true;
        });
      if (candidates.length === 0) return;
      const selectedCards = shuffledItems(candidates).slice(0, Math.min(count, 12))
        .map((template) => template.content.querySelector('[data-product-card]'));
      grid.replaceChildren(...selectedCards);
      randomizedProductRails.add(rail);
    });
  };

  const isExposed = (element) =>
    Boolean(element && !element.hidden && !element.closest('[aria-hidden="true"], [hidden], [inert]'));

  const focusServerErrorSummary = (root = document) => {
    const summary = elementsInRoot(root, '[data-error-summary]')
      .find((element) => !element.hidden && element.textContent.trim());
    if (!summary || focusedErrorSummaries.has(summary) || !isExposed(summary)) return;
    focusedErrorSummaries.add(summary);
    window.requestAnimationFrame(() => {
      if (summary.isConnected && isExposed(summary)) summary.focus({ preventScroll: true });
    });
  };

  const getCurrentVariantInput = (form) =>
    form?.querySelector('[data-variant-id-input]');

  const parseInteger = (value) => {
    const parsed = Number.parseInt(value || '', 10);
    return Number.isInteger(parsed) ? parsed : null;
  };
  const productSelectionContracts = Object.freeze({
    orderOptionSelections: (groups) => {
      const ordered = [...(groups || [])]
        .map((group, index) => ({ ...group, index }))
        .sort((left, right) => Number(left.position) - Number(right.position) || left.index - right.index);
      const selected = ordered.map(({ selection }) => selection);
      return selected.length > 0 && selected.every(Boolean) ? selected : null;
    },

    buildRequestUrl: ({ targetProductUrl, pageUrl, sectionId, optionIds, sellingPlan }) => {
      if (!targetProductUrl || !pageUrl || !sectionId || !Array.isArray(optionIds) || optionIds.length === 0) return null;
      const page = new URL(pageUrl);
      const request = new URL(targetProductUrl, page.origin);
      const targetSellingPlan = request.searchParams.has('selling_plan')
        ? request.searchParams.get('selling_plan')
        : page.searchParams.get('selling_plan');
      const transientParameters = new Set(['variant', 'option_values', 'section_id', 'selling_plan']);
      for (const [key, value] of page.searchParams) {
        if (!transientParameters.has(key) && !request.searchParams.has(key)) request.searchParams.append(key, value);
      }
      request.searchParams.delete('variant');
      request.searchParams.delete('option_values');
      request.searchParams.delete('section_id');
      request.searchParams.delete('selling_plan');
      request.searchParams.set('section_id', sectionId);
      request.searchParams.set('option_values', optionIds.join(','));
      const retainedSellingPlan = sellingPlan === undefined ? targetSellingPlan : sellingPlan;
      if (retainedSellingPlan) request.searchParams.set('selling_plan', retainedSellingPlan);
      return request.toString();
    },

    buildNavigationHref: ({ targetProductUrl, pageUrl, optionIds, sellingPlan }) => {
      if (!targetProductUrl || !pageUrl || !Array.isArray(optionIds) || optionIds.length === 0) return null;
      const page = new URL(pageUrl);
      const next = new URL(targetProductUrl, page.origin);
      const targetSellingPlan = next.searchParams.has('selling_plan')
        ? next.searchParams.get('selling_plan')
        : page.searchParams.get('selling_plan');
      const transientParameters = new Set(['variant', 'option_values', 'section_id', 'selling_plan']);
      for (const [key, value] of page.searchParams) {
        if (!transientParameters.has(key) && !next.searchParams.has(key)) next.searchParams.append(key, value);
      }
      next.searchParams.delete('variant');
      next.searchParams.delete('option_values');
      next.searchParams.delete('section_id');
      next.searchParams.delete('selling_plan');
      next.searchParams.set('option_values', optionIds.join(','));
      const retainedSellingPlan = sellingPlan === undefined ? targetSellingPlan : sellingPlan;
      if (retainedSellingPlan) next.searchParams.set('selling_plan', retainedSellingPlan);
      next.hash = page.hash;
      return `${next.pathname}${next.search}${next.hash}`;
    },

    buildHistoryHref: ({
      currentUrl,
      productUrl,
      variantUrl,
      variantId,
      optionIds,
      selectedSellingPlan = '',
      updateUrl = false
    }) => {
      if (updateUrl !== true || !productUrl || !currentUrl) return null;
      const current = new URL(currentUrl);
      const next = new URL(variantId && variantUrl ? variantUrl : productUrl, current.origin);
      const transientParameters = new Set(['section_id', 'variant', 'option_values', 'selling_plan']);
      for (const [key, value] of current.searchParams) {
        if (!transientParameters.has(key) && !next.searchParams.has(key)) next.searchParams.append(key, value);
      }
      next.searchParams.delete('section_id');
      next.searchParams.delete('selling_plan');
      if (selectedSellingPlan) next.searchParams.set('selling_plan', selectedSellingPlan);
      if (variantId && variantUrl) {
        next.searchParams.delete('option_values');
      } else {
        next.searchParams.delete('variant');
        next.searchParams.set('option_values', (optionIds || []).join(','));
      }
      next.hash = current.hash;
      return `${next.pathname}${next.search}${next.hash}`;
    },

    normalizeProductPath: (value, baseUrl) => {
      if (!value || !baseUrl) return '';
      try {
        const base = new URL(baseUrl);
        const candidate = new URL(value, base);
        if (candidate.origin !== base.origin) return '';
        return candidate.pathname.replace(/\/+$/, '') || '/';
      } catch (_error) {
        return '';
      }
    },

    nextGeneration: (generation) => (Number.isInteger(generation) ? generation : 0) + 1,

    beginRequest: (state) => {
      if (!state) return null;
      state.controller?.abort();
      state.generation = productSelectionContracts.nextGeneration(state.generation);
      return state.generation;
    },

    isCurrentRequest: ({ sameState, currentGeneration, generation, connected }) =>
      Boolean(sameState && connected && currentGeneration === generation),

    isValidReplacement: ({
      expectedSectionId,
      sectionId,
      expectedProductUrl,
      productUrl,
      pageUrl,
      hasProductSelection,
      hasCurrentVariantId,
      hasCurrentVariantUrl,
      hasCurrentMediaId,
      hasSelectedOptionIds,
      hasUpdateUrl,
      hasForm,
      hasEssentialControls = true,
      hasVariantInput = true,
      hasAddButton = true,
      hasQuantityInput = true,
      hasSellingPlanInput = true,
      hasOptionSelections = true,
      hasOptionGroupCount = true,
      hasNullVariantState = true,
      hasVariantInputState = true,
      hasPurchaseControlState = true
    }) => {
      const expectedProductPath = productSelectionContracts.normalizeProductPath(expectedProductUrl, pageUrl);
      const replacementProductPath = productSelectionContracts.normalizeProductPath(productUrl, pageUrl);
      const hasEssentialFormControls = [
        hasEssentialControls,
        hasVariantInput,
        hasAddButton,
        hasQuantityInput,
        hasSellingPlanInput,
        hasOptionSelections,
        hasOptionGroupCount,
        hasNullVariantState,
        hasVariantInputState,
        hasPurchaseControlState
      ].every((value) => value === true);
      return sectionId === expectedSectionId &&
        hasProductSelection === true &&
        Boolean(expectedProductPath && replacementProductPath && expectedProductPath === replacementProductPath) &&
        hasCurrentVariantId === true &&
        hasCurrentVariantUrl === true &&
        hasCurrentMediaId === true &&
        hasSelectedOptionIds === true &&
        hasUpdateUrl === true &&
        hasForm === true &&
        hasEssentialFormControls;
    },
    isValidOptionGroups: ({
      expectedCount,
      groups,
      isNullVariant = false,
      expectedGroups = [],
      requireCompatibleIdentity = false
    } = {}) => {
      if (
        !Number.isInteger(expectedCount) ||
        expectedCount < 0 ||
        !Array.isArray(groups) ||
        groups.length !== expectedCount
      ) return false;
      const hasValidGroups = groups.every(({
        controlCount,
        selectedCount,
        valueCount,
        position,
        name,
        controlsValid
      } = {}) =>
        Number.isInteger(controlCount) &&
        controlCount > 0 &&
        Number.isInteger(selectedCount) &&
        selectedCount >= 0 &&
        Number.isInteger(valueCount) &&
        valueCount === controlCount &&
        typeof position === 'string' &&
        position.trim() !== '' &&
        typeof name === 'string' &&
        name.trim() !== '' &&
        controlsValid === true &&
        (isNullVariant === true ? selectedCount === 0 : selectedCount > 0)
      );
      if (!hasValidGroups || requireCompatibleIdentity !== true) return hasValidGroups;
      if (!Array.isArray(expectedGroups) || expectedGroups.length !== groups.length) return false;
      const identity = (group) => `${group.position}\u0000${group.name}`;
      const expectedIdentity = expectedGroups.map(identity).sort().join('\u0001');
      const actualIdentity = groups.map(identity).sort().join('\u0001');
      if (expectedIdentity !== actualIdentity) return false;
      return expectedGroups.every((expectedGroup) => {
        const actualGroup = groups.find((group) => identity(group) === identity(expectedGroup));
        return Boolean(
          actualGroup &&
          Array.isArray(expectedGroup.controlIds) &&
          Array.isArray(actualGroup.controlIds) &&
          actualGroup.valueCount === expectedGroup.valueCount &&
          actualGroup.controlIds.length === expectedGroup.controlIds.length &&
          actualGroup.controlIds.every((id, index) => id === expectedGroup.controlIds[index])
        );
      });
    },

    findFocusCandidate: (controls, descriptor) => {
      if (!descriptor) return null;
      const elementFor = (entry) => entry?.element ?? entry;
      if (descriptor.optionValueId) {
        const byId = (controls || []).find(({ optionValueId }) => optionValueId === descriptor.optionValueId);
        if (byId) return elementFor(byId);
      }
      if (descriptor.optionPosition) {
        const samePosition = (controls || []).filter(({ optionPosition }) => optionPosition === descriptor.optionPosition);
        const byName = samePosition.find(({ optionName }) =>
          !descriptor.optionName || optionName === descriptor.optionName
        );
        if (byName) return elementFor(byName);
        if (samePosition[0]) return elementFor(samePosition[0]);
      }
      return null;
    },

    findFocusFallback: (candidates) => {
      const match = (candidates || []).find((entry) => entry && !entry.disabled && entry.exposed !== false);
      return match?.element ?? match ?? null;
    },

    purchaseState: ({ variantId, available, requiresSellingPlan, selectedPlan, quantityMin, quantityMax }) => {
      const hasVariant = Boolean(variantId);
      const min = Number.isFinite(quantityMin) ? quantityMin : 1;
      const quantityCanBeSatisfied = quantityMax === null || quantityMax === undefined || quantityMax >= min;
      return {
        hasVariant,
        quantityCanBeSatisfied,
        canPurchase: hasVariant && available === true && quantityCanBeSatisfied &&
          (!requiresSellingPlan || Boolean(selectedPlan))
      };
    }
  });


  const announce = (message) => {
    const region = document.getElementById('theme-live-region');
    if (!region) return;
    region.textContent = '';
    window.requestAnimationFrame(() => {
      region.textContent = message;
    });
  };

  const overlayTriggers = (name, overlay) => {
    const header = overlay?.closest('[data-site-header]');
    return header?.querySelectorAll(`[data-overlay-open="${CSS.escape(name)}"]`) ||
      document.querySelectorAll(`[data-overlay-open="${CSS.escape(name)}"]`);
  };

  const restoreBackgroundInert = () => {
    inertBackgroundState.forEach((previous, element) => {
      element.inert = previous.property;
      element.toggleAttribute('inert', previous.attribute);
    });
    inertBackgroundState.clear();
  };

  const markBackgroundInert = (element) => {
    if (!element || inertBackgroundState.has(element)) return;
    inertBackgroundState.set(element, {
      property: element.inert,
      attribute: element.hasAttribute('inert')
    });
    element.inert = true;
    element.setAttribute('inert', '');
  };

  const inertOverlayBackground = (overlay) => {
    restoreBackgroundInert();
    let branch = overlay;
    let parent = overlay.parentElement;
    while (parent) {
      [...parent.children].forEach((sibling) => {
        if (sibling !== branch && !sibling.matches?.('.overlay-backdrop')) markBackgroundInert(sibling);
      });
      if (parent === document.body) break;
      branch = parent;
      parent = parent.parentElement;
    }
  };

  const setOverlayState = (overlay, isOpen) => {
    overlay.toggleAttribute('data-open', isOpen);
    overlay.inert = !isOpen;
    overlay.toggleAttribute('inert', !isOpen);
    if (isOpen) {
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-hidden', 'false');
    } else {
      overlay.removeAttribute('role');
      overlay.removeAttribute('aria-modal');
      overlay.setAttribute('aria-hidden', 'true');
    }
    overlayTriggers(overlay.dataset.overlay, overlay).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  };

  const isVisibleFocusTarget = (target) => {
    if (!target?.isConnected || target.closest('[aria-hidden="true"], [hidden], [inert]')) return false;
    const style = window.getComputedStyle(target);
    return target.getClientRects().length > 0 && style.display !== 'none' && style.visibility !== 'hidden';
  };
  const focusDescriptor = (element, root) => {
    if (!element || !root?.contains(element)) return null;
    return {
      id: element.id || '',
      cartKey: element.dataset?.cartKey || '',
      key: element.dataset?.key || '',
      name: element.getAttribute('name') || '',
      href: element.getAttribute('href') || '',
      closeControl: element.matches('[data-overlay-close]')
    };
  };

  const findFocusEquivalent = (root, descriptor) => {
    if (!root || !descriptor) return null;
    if (descriptor.cartKey) {
      const byCartKey = root.querySelector(`[data-cart-key="${CSS.escape(descriptor.cartKey)}"]`);
      if (byCartKey) return byCartKey;
    }
    if (descriptor.key) {
      const byKey = root.querySelector(`[data-key="${CSS.escape(descriptor.key)}"]`);
      if (byKey) return byKey;
    }
    if (descriptor.id) {
      const byId = root.querySelector(`#${CSS.escape(descriptor.id)}`);
      if (byId) return byId;
    }
    if (descriptor.closeControl) return root.querySelector('[data-overlay-close]');
    if (descriptor.name) {
      const byName = root.querySelector(`[name="${CSS.escape(descriptor.name)}"]`);
      if (byName) return byName;
    }
    if (descriptor.href) {
      const byHref = root.querySelector(`a[href="${CSS.escape(descriptor.href)}"]`);
      if (byHref) return byHref;
    }
    return root.querySelector(focusableSelector);
  };

  const clearDetachedOverlayState = (overlay) => {
    if (!overlay || overlay.isConnected) return;
    if (openOverlayElement !== overlay) return;
    restoreBackgroundInert();
    openOverlayElement = null;
    openOverlayTrigger = null;
    overlayStateGeneration += 1;
    document.body.classList.remove('overlay-open');
  };

  const closeOverlay = ({ restoreFocus = true } = {}) => {
    if (!openOverlayElement) return;
    const overlay = openOverlayElement;
    const trigger = openOverlayTrigger;
    setOverlayState(overlay, false);
    restoreBackgroundInert();
    openOverlayElement = null;
    openOverlayTrigger = null;
    overlayStateGeneration += 1;
    document.body.classList.remove('overlay-open');
    if (window.location.hash) {
      const target = document.getElementById(window.location.hash.slice(1));
      if (target?.dataset.overlay) {
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
      }
    }
    if (restoreFocus) {
      const focusTarget = isVisibleFocusTarget(trigger)
        ? trigger
        : document.getElementById('main-content');
      focusTarget?.focus({ preventScroll: true });
    }
  };

  const initialOverlayControl = (overlay) => {
    if (overlay.dataset.overlay === 'search') {
      const searchInput = overlay.querySelector('[data-search-overlay-input]');
      if (searchInput) return searchInput;
    }
    const bodyControl = overlay.querySelector('.overlay__body')?.querySelector(focusableSelector);
    return bodyControl || overlay.querySelector('[data-overlay-close]') || overlay.querySelector(focusableSelector);
  };

  const openOverlay = (name, trigger = null) => {
    const header = trigger?.closest('[data-site-header]');
    const overlay = header?.querySelector(`[data-overlay="${CSS.escape(name)}"]`) ||
      document.querySelector(`[data-overlay="${CSS.escape(name)}"]`);
    if (!overlay) return;
    const overlayHeader = header || overlay.closest('[data-site-header]');
    setHeaderVisibility(overlayHeader, false);
    if (openOverlayElement && openOverlayElement !== overlay) closeOverlay({ restoreFocus: false });
    openOverlayElement = overlay;
    openOverlayTrigger = trigger;
    overlayStateGeneration += 1;
    setOverlayState(overlay, true);
    inertOverlayBackground(overlay);
    document.body.classList.add('overlay-open');
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
      if (openOverlayElement !== overlay) return;
      initialOverlayControl(overlay)?.focus({ preventScroll: true });
    }));
  };

  const trapFocus = (event) => {
    if (event.key !== 'Tab' || !openOverlayElement) return;
    const focusable = [...openOverlayElement.querySelectorAll(focusableSelector)].filter(isVisibleFocusTarget);
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (!openOverlayElement.contains(active)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const jsonEndpoint = (route) => {
    const url = new URL(route, window.location.origin);
    if (!url.pathname.endsWith('.js')) url.pathname = `${url.pathname}.js`;
    return url.toString();
  };

  const READ_ONLY_TIMEOUT_MS = 10000;
  const WRITE_WATCHDOG_TIMEOUT_MS = 15000;
  const CUSTOMER_MESSAGE_MAX_LENGTH = 240;
  const CUSTOMER_MESSAGE_CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;

  const isBoundedCustomerMessage = (value) => {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    return trimmed.length > 0 &&
      trimmed.length <= CUSTOMER_MESSAGE_MAX_LENGTH &&
      !CUSTOMER_MESSAGE_CONTROL_CHARACTERS.test(trimmed);
  };

  const localizedMessage = (...candidates) => {
    const fallback = document.body?.dataset?.generalError;
    return [...candidates, fallback]
      .find((candidate) => isBoundedCustomerMessage(candidate))
      ?.trim() || '';
  };

  class ReadOnlyTimeoutError extends Error {
    constructor() {
      super('read-only request timed out');
      this.name = 'TimeoutError';
      this.code = 'READ_ONLY_TIMEOUT';
    }
  }

  class UnknownWriteOutcomeError extends Error {
    constructor() {
      super('write result unknown');
      this.name = 'UnknownWriteOutcomeError';
      this.code = 'WRITE_OUTCOME_UNKNOWN';
    }
  }

  const makeAbortError = () => {
    const error = new Error('request aborted');
    error.name = 'AbortError';
    return error;
  };

  const fetchReadOnly = async (url, { signal, timeout = READ_ONLY_TIMEOUT_MS, ...options } = {}) => {
    const controller = new AbortController();
    let callerAborted = Boolean(signal?.aborted);
    let timedOut = false;
    let timeoutId = null;
    let bodyPending = false;
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      signal?.removeEventListener?.('abort', abortFromCaller);
    };
    const abortFromCaller = () => {
      callerAborted = true;
      controller.abort();
      cleanup();
    };
    if (callerAborted) {
      controller.abort();
    } else {
      signal?.addEventListener?.('abort', abortFromCaller, { once: true });
    }
    const duration = Number.isFinite(timeout) && timeout > 0
      ? Math.min(timeout, READ_ONLY_TIMEOUT_MS)
      : READ_ONLY_TIMEOUT_MS;
    timeoutId = window.setTimeout(() => {
      if (callerAborted || controller.signal.aborted) return;
      timedOut = true;
      controller.abort();
      cleanup();
    }, duration);

    try {
      if (callerAborted) throw makeAbortError();
      const response = await fetch(url, { ...options, signal: controller.signal });
      if (timedOut) throw new ReadOnlyTimeoutError();
      if (callerAborted) throw makeAbortError();
      if (response?.ok !== false) {
        ['text', 'json'].forEach((method) => {
          const original = response?.[method];
          if (typeof original !== 'function') return;
          const wrapped = (...args) => Promise.resolve()
            .then(() => original.apply(response, args))
            .then((value) => {
              if (timedOut) throw new ReadOnlyTimeoutError();
              if (callerAborted || signal?.aborted) throw makeAbortError();
              cleanup();
              return value;
            }, (error) => {
              cleanup();
              if (timedOut) throw new ReadOnlyTimeoutError();
              if (callerAborted || signal?.aborted) throw makeAbortError();
              throw error;
            });
          try {
            response[method] = wrapped;
            if (response[method] === wrapped) bodyPending = true;
          } catch (_error) {
            // A non-extensible response still has a bounded fetch deadline.
          }
        });
      }
      return response;
    } catch (error) {
      if (timedOut) throw new ReadOnlyTimeoutError();
      if (callerAborted || signal?.aborted) throw makeAbortError();
      throw error;
    } finally {
      if (!bodyPending) cleanup();
    }
  };

  const isMutationResponseObject = (value) =>
    Boolean(value && typeof value === 'object' && !Array.isArray(value));

  const setWriteReconciliationVisible = (form, visible) => {
    const link = form?.querySelector?.('[data-write-reconcile-link]');
    if (!link) return;
    link.hidden = !visible;
    link.setAttribute('aria-hidden', String(!visible));
  };

  const writeUnknownMessage = (form) =>
    localizedMessage(form?.dataset?.writeUnknownMessage, document.body?.dataset?.writeUnknown);

  const clearWriteWatchdog = (form) => {
    const timer = writeWatchdogs.get(form);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      writeWatchdogs.delete(form);
    }
  };

  const startWriteWatchdog = (form, onTimeout, timeout = WRITE_WATCHDOG_TIMEOUT_MS) => {
    clearWriteWatchdog(form);
    const duration = Number.isFinite(timeout) && timeout > 0
      ? Math.min(timeout, WRITE_WATCHDOG_TIMEOUT_MS)
      : WRITE_WATCHDOG_TIMEOUT_MS;
    const timer = window.setTimeout(() => {
      writeWatchdogs.delete(form);
      if (!form?.isConnected || form.dataset.pending !== 'true' || form.dataset.writeOutcome === 'unknown') return;
      form.dataset.writeOutcome = 'unknown';
      setWriteReconciliationVisible(form, true);
      onTimeout?.();
    }, duration);
    writeWatchdogs.set(form, timer);
  };

  const beginWriteWatchdog = (form, onTimeout) => {
    delete form.dataset.writeOutcome;
    setWriteReconciliationVisible(form, false);
    startWriteWatchdog(form, onTimeout);
  };
  const markWriteUnknown = (form, showError) => {
    if (!form) return;
    form.dataset.writeOutcome = 'unknown';
    setWriteReconciliationVisible(form, true);
    showError?.(form, writeUnknownMessage(form));
  };

  const clearWriteOutcome = (form) => {
    clearWriteWatchdog(form);
    if (!form) return;
    delete form.dataset.writeOutcome;
    setWriteReconciliationVisible(form, false);
  };

  const finishWrite = (form, setPending) => {
    clearWriteWatchdog(form);
    if (form?.dataset?.writeOutcome === 'unknown') {
      if (form.isConnected) setPending(form, true);
      return false;
    }
    clearWriteOutcome(form);
    if (form?.isConnected) setPending(form, false);
    return true;
  };
  const cleanupWriteWatchdogs = (root = null) => {
    elementsInRoot(root || document, '[data-product-form], [data-cart-form]')
      .forEach((form) => clearWriteWatchdog(form));
  };
  const writeOutcomeContracts = Object.freeze({
    shouldPreserveUnknown: ({ committed = false, writeOutcome } = {}) =>
      committed === true || writeOutcome === 'unknown',
    shouldClearErrorAfterReconciliation: ({ reconciled } = {}) => reconciled === true
  });

  const cartMarkupContracts = Object.freeze({
    isValidEnvelope: ({
      markup,
      expectedRootId,
      rootId,
      expectedSectionId,
      sectionId,
      expectedSectionUrl,
      sectionUrl,
      expectedOverlay,
      overlay,
      rootCount,
      bodyType,
      hasFormShape = true
    } = {}) => typeof markup === 'string' &&
      markup.trim() !== '' &&
      typeof expectedRootId === 'string' &&
      expectedRootId.trim() !== '' &&
      rootId === expectedRootId &&
      typeof expectedSectionId === 'string' &&
      expectedSectionId.trim() !== '' &&
      sectionId === expectedSectionId &&
      typeof expectedSectionUrl === 'string' &&
      expectedSectionUrl.trim() !== '' &&
      sectionUrl === expectedSectionUrl &&
      typeof expectedOverlay === 'string' &&
      expectedOverlay.trim() !== '' &&
      overlay === expectedOverlay &&
      rootCount === 1 &&
      (
        bodyType === 'empty' ||
        (bodyType === 'form' && hasFormShape === true)
      ),
    shouldUseFallback: ({ bundledResult } = {}) => bundledResult === null,
    reconciliationRequestCount: ({ bundledResult } = {}) =>
      bundledResult === null ? 1 : 0
  });

  const recommendationContracts = Object.freeze({
    isValidContent: ({
      contentCount = 1,
      hasList = false,
      hasItems = false,
      hasEmptyMarker = false
    } = {}) =>
      contentCount === 1 &&
      (
        (hasList === true && hasItems === true && hasEmptyMarker === false) ||
        (hasList === false && hasItems === false && hasEmptyMarker === true)
      ),
    canRetry: ({ connected, state, started, retryVisible } = {}) =>
      connected === true && state === 'error' && started === true && retryVisible === true
  });

  const errorMessage = async (response, fallback) => {
    const localizedFallback = localizedMessage(fallback);
    try {
      const payload = await response.json();
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return localizedFallback;
      const candidate = typeof payload.description === 'string'
        ? payload.description
        : typeof payload.message === 'string'
          ? payload.message
          : null;
      return isBoundedCustomerMessage(candidate) ? candidate.trim() : localizedFallback;
    } catch (_error) {
      return localizedFallback;
    }
  };

  const cartSectionRequest = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return null;
    const sectionId = drawer.dataset.sectionId;
    const sectionUrl = drawer.dataset.sectionUrl;
    const overlay = drawer.dataset.overlay;
    if (
      typeof sectionId !== 'string' ||
      sectionId.trim() === '' ||
      typeof sectionUrl !== 'string' ||
      sectionUrl.trim() === '' ||
      typeof overlay !== 'string' ||
      overlay.trim() === ''
    ) return null;
    return {
      drawer,
      sectionId,
      sectionUrl,
      overlay,
      sectionsUrl: `${window.location.pathname}${window.location.search}`
    };
  };

  const parseCartDrawerMarkup = (markup, sourceDrawer) => {
    if (!sourceDrawer || typeof markup !== 'string' || markup.trim() === '') return null;
    let parsed;
    try {
      parsed = new DOMParser().parseFromString(markup, 'text/html');
    } catch (_error) {
      return null;
    }
    const roots = [...parsed.querySelectorAll('[data-cart-drawer]')];
    if (roots.length !== 1) return null;
    const replacement = roots[0];
    const body = [...replacement.querySelectorAll('.cart-drawer__body')];
    if (body.length !== 1) return null;
    const forms = [...body[0].querySelectorAll('form[data-cart-form]')];
    const emptyMarkers = [...body[0].querySelectorAll('[data-cart-empty]')];
    const form = forms.length === 1 ? forms[0] : null;
    const quantities = form ? [...form.querySelectorAll('input[name="updates[]"]')] : [];
    const lineQuantities = form
      ? quantities.filter((input) => typeof input.dataset.cartKey === 'string' && input.dataset.cartKey.trim() !== '')
      : [];
    const removeLinks = form
      ? [...form.querySelectorAll('[data-cart-remove][data-key]')]
        .filter((link) => typeof link.dataset.key === 'string' && link.dataset.key.trim() !== '')
      : [];
    const actionControls = form
      ? [...form.querySelectorAll('button[name="update"], button[name="checkout"]')]
      : [];
    const hasFormShape = Boolean(
      form &&
      typeof form.getAttribute('action') === 'string' &&
      form.getAttribute('action').trim() !== '' &&
      typeof form.dataset.cartUpdateUrl === 'string' &&
      form.dataset.cartUpdateUrl.trim() !== '' &&
      typeof form.dataset.cartChangeUrl === 'string' &&
      form.dataset.cartChangeUrl.trim() !== '' &&
      quantities.length > 0 &&
      quantities.length === lineQuantities.length &&
      removeLinks.length === lineQuantities.length &&
      actionControls.length > 0
    );
    const bodyType = forms.length === 1 && emptyMarkers.length === 0
      ? 'form'
      : forms.length === 0 && emptyMarkers.length === 1
        ? 'empty'
        : null;
    if (!cartMarkupContracts.isValidEnvelope({
      markup,
      expectedRootId: sourceDrawer.id,
      rootId: replacement.id,
      expectedSectionId: sourceDrawer.dataset.sectionId,
      sectionId: replacement.dataset.sectionId,
      expectedSectionUrl: sourceDrawer.dataset.sectionUrl,
      sectionUrl: replacement.dataset.sectionUrl,
      expectedOverlay: sourceDrawer.dataset.overlay,
      overlay: replacement.dataset.overlay,
      rootCount: roots.length,
      bodyType,
      hasFormShape
    })) return null;
    return { parsed, replacement };
  };

  const addBundledCartSections = (body, request = cartSectionRequest()) => {
    if (!request || !body) return body;
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      body.set('sections', request.sectionId);
      body.set('sections_url', request.sectionsUrl);
    } else if (typeof body === 'object') {
      body.sections = request.sectionId;
      body.sections_url = request.sectionsUrl;
    }
    return body;
  };

  const replaceCartDrawerSection = (
    markup,
    { open = true, trigger = null, openGeneration = null, initialDrawer = null, requestGeneration = null } = {}
  ) => {
    const sourceDrawer = initialDrawer || document.querySelector('[data-cart-drawer]');
    const parsedMarkup = parseCartDrawerMarkup(markup, sourceDrawer);
    if (!parsedMarkup) {
      throw new Error(localizedMessage(document.body?.dataset?.cartUpdateError));
    }
    const { parsed, replacement } = parsedMarkup;
    const generation = requestGeneration === null ? ++cartRefreshGeneration : requestGeneration;
    const initialOverlayGeneration = overlayStateGeneration;
    const previousTrigger = openOverlayTrigger;
    const preservedFocus = focusDescriptor(document.activeElement, sourceDrawer);
    if (generation !== cartRefreshGeneration) return false;

    const liveDrawer = document.querySelector('[data-cart-drawer]');
    if (!liveDrawer) {
      clearDetachedOverlayState(sourceDrawer);
      return false;
    }
    const targetDrawer = liveDrawer;
    if (liveDrawer !== sourceDrawer &&
      (liveDrawer.dataset.sectionId !== sourceDrawer.dataset.sectionId ||
        liveDrawer.dataset.sectionUrl !== sourceDrawer.dataset.sectionUrl ||
        liveDrawer.dataset.overlay !== sourceDrawer.dataset.overlay)) {
      clearDetachedOverlayState(sourceDrawer);
      return false;
    }
    const drawerWasOpen = openOverlayElement === targetDrawer ||
      (targetDrawer !== sourceDrawer &&
        openOverlayElement === sourceDrawer &&
        overlayStateGeneration === initialOverlayGeneration);
    const activeFocus = preservedFocus || focusDescriptor(document.activeElement, targetDrawer);

    const nextCount = parsed.querySelector('[data-cart-count]');
    if (nextCount) {
      document.querySelectorAll('[data-cart-count]').forEach((count) => {
        count.textContent = nextCount.textContent;
        count.setAttribute('aria-label', nextCount.getAttribute('aria-label') || '');
      });
    }

    targetDrawer.replaceWith(replacement);
    if (drawerWasOpen) {
      openOverlayElement = replacement;
      openOverlayTrigger = openOverlayTrigger || previousTrigger;
      setOverlayState(replacement, true);
    } else {
      setOverlayState(replacement, false);
    }
    const shouldOpen = open && !drawerWasOpen && Boolean(trigger) &&
      (openGeneration === null || openGeneration === overlayStateGeneration);
    if (shouldOpen) {
      openOverlay('cart', trigger);
    } else if (drawerWasOpen && activeFocus) {
      window.requestAnimationFrame(() => {
        if (openOverlayElement !== replacement) return;
        findFocusEquivalent(replacement, activeFocus)?.focus({ preventScroll: true });
      });
    }
    return true;
  };

  const applyBundledCartSection = (response, options = {}) => {
    const request = cartSectionRequest();
    if (!request) return null;
    const sections = response?.sections;
    if (!sections || typeof sections !== 'object' || Array.isArray(sections)) return null;
    if (!Object.prototype.hasOwnProperty.call(sections, request.sectionId)) return null;
    const markup = sections[request.sectionId];
    if (!parseCartDrawerMarkup(markup, request.drawer)) return null;
    cartRefreshController?.abort();
    cartRefreshController = null;
    return replaceCartDrawerSection(markup, { ...options, initialDrawer: request.drawer });
  };

  const refreshCartAfterMutation = async (response, options = {}) => {
    const bundled = applyBundledCartSection(response, options);
    if (!cartMarkupContracts.shouldUseFallback({ bundledResult: bundled })) return bundled;
    return refreshCartDrawer(options);
  };

  const cancelCartDrawerRefresh = (root = null) => {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (root && drawer && root !== drawer && !root.contains?.(drawer)) return;
    cartRefreshController?.abort();
    cartRefreshController = null;
    cartRefreshGeneration += 1;
  };

  const refreshCartDrawer = async ({ open = true, trigger = null, openGeneration = null } = {}) => {
    const request = cartSectionRequest();
    if (!request) return false;
    const requestGeneration = ++cartRefreshGeneration;
    cartRefreshController?.abort();
    const controller = new AbortController();
    cartRefreshController = controller;

    try {
      const url = new URL(request.sectionUrl, window.location.origin);
      url.searchParams.set('section_id', request.sectionId);
      const response = await fetchReadOnly(url.toString(), {
        signal: controller.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (requestGeneration !== cartRefreshGeneration || controller.signal.aborted) return false;
      if (!response.ok) throw new Error(localizedMessage(document.body?.dataset?.cartUpdateError));
      const markup = await response.text();
      if (requestGeneration !== cartRefreshGeneration || controller.signal.aborted) return false;
      return replaceCartDrawerSection(markup, {
        open,
        trigger,
        openGeneration,
        initialDrawer: request.drawer,
        requestGeneration
      });
    } catch (error) {
      if (requestGeneration !== cartRefreshGeneration || error.name === 'AbortError') return false;
      throw error;
    } finally {
      if (cartRefreshController === controller) cartRefreshController = null;
    }
  };

  const updateCurrentPriceDisplay = (form, source) => {
    if (!source) return;
    const formId = form.getAttribute('id');
    const price = formId ? document.getElementById(`${formId}-Price`) : null;
    if (!price) return;
    const currentPrice = price.querySelector('.price__current');
    if (source.dataset.price && currentPrice) currentPrice.textContent = source.dataset.price;

    let comparePrice = price.querySelector('.price__compare');
    let comparePriceLabel = price.querySelector('[data-compare-price-label]');
    if (source.dataset.comparePrice) {
      if (!comparePrice) {
        comparePrice = document.createElement('s');
        comparePrice.className = 'price__compare';
        price.insertBefore(comparePrice, currentPrice || null);
      }
      if (!comparePriceLabel) {
        comparePriceLabel = document.createElement('span');
        comparePriceLabel.className = 'visually-hidden';
        comparePriceLabel.dataset.comparePriceLabel = '';
        price.insertBefore(comparePriceLabel, comparePrice || currentPrice || null);
      }
      comparePrice.textContent = source.dataset.comparePrice;
      comparePriceLabel.textContent = source.dataset.comparePriceLabel || comparePriceLabel.textContent;
      comparePriceLabel.hidden = false;
    } else {
      comparePrice?.remove();
      if (comparePriceLabel) comparePriceLabel.hidden = true;
    }

    let unitPrice = price.querySelector('.unit-price');
    if (source.dataset.unitPrice) {
      if (!unitPrice) {
        unitPrice = document.createElement('small');
        unitPrice.className = 'unit-price';
        price.append(unitPrice);
      }
      const unitPriceLabel = document.createElement('span');
      unitPriceLabel.className = 'visually-hidden';
      unitPriceLabel.textContent = source.dataset.unitPriceLabel || '';
      unitPrice.replaceChildren(unitPriceLabel, document.createTextNode(source.dataset.unitPrice));
    } else {
      unitPrice?.remove();
    }
  };

  const normalizeQuantityInput = (quantity, candidate = quantity?.value) => {
    if (!quantity) return;
    const min = parseInteger(quantity.min) ?? 1;
    const step = parseInteger(quantity.step) || 1;
    const max = parseInteger(quantity.max);
    const current = parseInteger(candidate) ?? min;
    const bounded = Math.max(min, current);
    let normalized = min + Math.floor((bounded - min) / step) * step;
    if (max !== null && normalized > max) {
      normalized = max >= min ? min + Math.floor((max - min) / step) * step : min;
    }
    quantity.value = String(normalized);
  };

  const initializeCurrentQuantity = (form) => {
    normalizeQuantityInput(form.querySelector('input[name="quantity"]'));
  };

  const getCurrentPurchaseState = (form) => {
    const variantInput = getCurrentVariantInput(form);
    const hasVariant = Boolean(variantInput?.value);
    const available = hasVariant && variantInput.dataset.available === 'true';
    const requiresPlan = hasVariant && variantInput.dataset.requiresSellingPlan === 'true';
    const selectedPlan = form.querySelector('[data-selling-plan-input]')?.value || '';
    const quantity = form.querySelector('input[name="quantity"]');
    const min = parseInteger(quantity?.min) ?? 1;
    const max = parseInteger(quantity?.max);
    return productSelectionContracts.purchaseState({
      variantId: variantInput?.value || '',
      available,
      requiresSellingPlan: requiresPlan,
      selectedPlan,
      quantityMin: min,
      quantityMax: max
    });
  };

  const updateCurrentPurchaseState = (form) => {
    const button = form.querySelector('[data-add-to-cart]');
    const purchaseState = getCurrentPurchaseState(form);
    if (!button) return purchaseState;
    const variantInput = getCurrentVariantInput(form);
    const available = variantInput?.dataset.available === 'true';
    const requiresPlan = variantInput?.dataset.requiresSellingPlan === 'true';
    const selectedPlan = form.querySelector('[data-selling-plan-input]')?.value || '';

    button.disabled = !purchaseState.canPurchase;
    const paymentButton = form.querySelector('.shopify-payment-button');
    if (paymentButton) paymentButton.inert = !purchaseState.canPurchase;
    const label = button.querySelector('[data-add-label]');
    if (!label) return purchaseState;
    label.textContent = !purchaseState.hasVariant
      ? variantInput?.dataset.unavailableLabel || document.body.dataset.productUnavailable || ''
      : !available
        ? variantInput.dataset.soldOutLabel || ''
        : requiresPlan && !selectedPlan
          ? variantInput.dataset.sellingPlanLabel || ''
          : variantInput.dataset.addLabel || '';
    return purchaseState;
  };

  const syncCurrentSellingPlan = (form) => {
    const sellingPlanInput = form.querySelector('[data-selling-plan-input]');
    const group = form.querySelector('[data-selling-plan-options]');
    if (!sellingPlanInput || !group) {
      updateCurrentPurchaseState(form);
      return '';
    }

    let selected = group.querySelector('[data-selling-plan-option]:checked:not(:disabled)');
    if (!selected) {
      const required = group.dataset.requiresSellingPlan === 'true';
      selected = required
        ? group.querySelector('[data-selling-plan-option][data-selling-plan-id]:not([data-selling-plan-id=""]):not(:disabled)')
        : group.querySelector('[data-selling-plan-option][data-selling-plan-id=""]:not(:disabled)');
      if (!selected) selected = group.querySelector('[data-selling-plan-option]:not(:disabled)');
      if (selected) selected.checked = true;
    }
    sellingPlanInput.value = selected?.dataset.sellingPlanId || '';
    updateCurrentPriceDisplay(form, selected || getCurrentVariantInput(form));
    updateCurrentPurchaseState(form);
    return sellingPlanInput.value;
  };

  const setProductPending = (form, pending) => {
    form.dataset.pending = pending ? 'true' : 'false';
    form.setAttribute('aria-busy', pending ? 'true' : 'false');
    form.querySelectorAll('button, input, select, textarea').forEach((control) => {
      if (pending) {
        if (!control.hasAttribute('data-product-was-disabled')) {
          control.dataset.productWasDisabled = control.disabled ? 'true' : 'false';
        }
        control.disabled = true;
      } else if (control.hasAttribute('data-product-was-disabled')) {
        control.disabled = control.dataset.productWasDisabled === 'true';
        delete control.dataset.productWasDisabled;
      }
    });
    form.querySelectorAll('.shopify-payment-button').forEach((control) => {
      if (pending) {
        if (!control.hasAttribute('data-product-was-inert')) {
          control.dataset.productWasInert = control.inert ? 'true' : 'false';
        }
        control.inert = true;
      } else if (control.hasAttribute('data-product-was-inert')) {
        control.inert = control.dataset.productWasInert === 'true';
        delete control.dataset.productWasInert;
      }
    });
  };

  const showProductError = (form, message) => {
    const visibleMessage = localizedMessage(message);
    const error = form?.querySelector('[data-product-error]');
    let visibleError = false;
    if (error) {
      error.textContent = visibleMessage;
      error.hidden = false;
      visibleError = isExposed(error);
      if (visibleError) error.focus();
    }
    if (!visibleError) announce(visibleMessage);
  };

  const clearProductError = (form) => {
    const error = form?.querySelector('[data-product-error]');
    if (!error) return;
    error.textContent = '';
    error.hidden = true;
  };

  const submitProductForm = async (form) => {
    if (form.dataset.pending === 'true') return;
    const button = form.querySelector('[data-add-to-cart]');
    const variantInput = getCurrentVariantInput(form);
    syncCurrentSellingPlan(form);
    const purchaseState = updateCurrentPurchaseState(form);
    if (!button || variantInput?.disabled || !purchaseState.canPurchase || button.disabled) {
      showProductError(
        form,
        localizedMessage(
          button?.querySelector('[data-add-label]')?.textContent?.trim(),
          variantInput?.dataset.unavailableLabel,
          document.body?.dataset?.productUpdateError,
          document.body?.dataset?.addError
        )
      );
      return;
    }
    const body = new FormData(form);
    form.querySelectorAll('[data-product-option-value], [data-selling-plan-option]').forEach((control) => {
      if (control.name) body.delete(control.name);
    });
    addBundledCartSections(body);
    const submittedOverlayGeneration = overlayStateGeneration;
    clearProductError(form);
    setProductPending(form, true);
    beginWriteWatchdog(form, () => showProductError(form, writeUnknownMessage(form)));
    let committed = false;
    let requestError = '';

    try {
      const response = await fetch(jsonEndpoint(form.action), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body
      });
      if (!response.ok) {
        requestError = await errorMessage(response, document.body?.dataset?.addError);
        throw new Error('product add rejected');
      }
      committed = true;
      let responseData = null;
      let validResponse = false;
      try {
        responseData = await response.json();
        validResponse = isMutationResponseObject(responseData);
      } catch (_error) {
        responseData = null;
      }
      const refreshed = await refreshCartAfterMutation(validResponse ? responseData : null, {
        open: true,
        trigger: button,
        openGeneration: submittedOverlayGeneration
      });
      if (!refreshed) throw new UnknownWriteOutcomeError();
      const title = validResponse && isBoundedCustomerMessage(responseData.product_title)
        ? responseData.product_title.trim()
        : validResponse && isBoundedCustomerMessage(responseData.title)
          ? responseData.title.trim()
          : '';
      const addedTemplate = localizedMessage(document.body?.dataset?.addedTemplate);
      const genericAdded = localizedMessage(document.body?.dataset?.addedGeneric);
      if (writeOutcomeContracts.shouldClearErrorAfterReconciliation({ reconciled: refreshed })) {
        clearProductError(form);
      }
      clearWriteOutcome(form);
      const personalizedAdded = title && addedTemplate ? addedTemplate.replace('[title]', () => title) : '';
      announce(isBoundedCustomerMessage(personalizedAdded) ? personalizedAdded : genericAdded);
    } catch (caughtError) {
      if (writeOutcomeContracts.shouldPreserveUnknown({
        committed,
        writeOutcome: form.dataset.writeOutcome
      })) {
        markWriteUnknown(form, showProductError);
      } else {
        clearWriteOutcome(form);
        showProductError(form, requestError || document.body?.dataset?.addError);
      }
    } finally {
      const settled = finishWrite(form, setProductPending);
      if (!form.isConnected) return;
      if (settled) {
        syncCurrentSellingPlan(form);
        updateCurrentPurchaseState(form);
      }
    }
  };

  const setCartPending = (form, pending) => {
    form.dataset.pending = pending ? 'true' : 'false';
    form.setAttribute('aria-busy', pending ? 'true' : 'false');
    form.querySelectorAll('button, input, select, textarea').forEach((control) => {
      if (pending) {
        if (!control.hasAttribute('data-was-disabled')) {
          control.dataset.wasDisabled = control.disabled ? 'true' : 'false';
        }
        control.disabled = true;
      } else if (control.hasAttribute('data-was-disabled')) {
        control.disabled = control.dataset.wasDisabled === 'true';
        delete control.dataset.wasDisabled;
      }
    });
    form.querySelectorAll('[data-cart-remove]').forEach((link) => {
      link.setAttribute('aria-disabled', pending ? 'true' : 'false');
    });
  };

  const showCartError = (form, message) => {
    const visibleMessage = localizedMessage(message);
    const error = form.querySelector('[data-cart-error]');
    const drawer = form.closest('[data-cart-drawer]');
    const drawerIsOpen = !drawer || (openOverlayElement === drawer && drawer.hasAttribute('data-open'));
    let visibleError = false;
    if (error) {
      error.textContent = visibleMessage;
      error.hidden = !drawerIsOpen;
      visibleError = drawerIsOpen && isExposed(error);
      if (visibleError) error.focus();
    }
    if (!visibleError) announce(visibleMessage);
  };
  const clearCartError = (form) => {
    const error = form.querySelector('[data-cart-error]');
    if (!error) return;
    error.textContent = '';
    error.hidden = true;
  };

  const submitCartForm = async (form) => {
    const route = form.dataset.cartUpdateUrl;
    if (!route || form.dataset.pending === 'true') return;
    const body = new FormData(form);
    addBundledCartSections(body);
    clearCartError(form);
    setCartPending(form, true);
    beginWriteWatchdog(form, () => showCartError(form, writeUnknownMessage(form)));
    let committed = false;
    let requestError = '';

    try {
      const response = await fetch(jsonEndpoint(route), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body
      });
      if (!response.ok) {
        requestError = await errorMessage(response, document.body?.dataset?.cartUpdateError);
        throw new Error('cart update rejected');
      }
      committed = true;
      if (form.dataset.cartSurface === 'page') {
        clearCartError(form);
        clearWriteOutcome(form);
        window.location.assign(form.action);
        return;
      }
      let responseData = null;
      try {
        responseData = await response.json();
      } catch (_error) {
        responseData = null;
      }
      const refreshed = await refreshCartAfterMutation(
        isMutationResponseObject(responseData) ? responseData : null,
        { open: true }
      );
      if (!refreshed) throw new UnknownWriteOutcomeError();
      clearCartError(form);
      clearWriteOutcome(form);
      announce(localizedMessage(document.body?.dataset?.cartUpdated));
    } catch (_error) {
      if (writeOutcomeContracts.shouldPreserveUnknown({
        committed,
        writeOutcome: form.dataset.writeOutcome
      })) {
        markWriteUnknown(form, showCartError);
      } else {
        clearWriteOutcome(form);
        showCartError(form, requestError || document.body?.dataset?.cartUpdateError);
      }
    } finally {
      finishWrite(form, setCartPending);
    }
  };

  const removeCartLine = async (link) => {
    const form = link.closest('[data-cart-form]');
    const route = form?.dataset.cartChangeUrl;
    if (!form || !route || form.dataset.pending === 'true') return;
    clearCartError(form);
    setCartPending(form, true);
    beginWriteWatchdog(form, () => showCartError(form, writeUnknownMessage(form)));
    const body = addBundledCartSections({ id: link.dataset.key, quantity: 0 });
    let committed = false;
    let requestError = '';

    try {
      const response = await fetch(jsonEndpoint(route), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        requestError = await errorMessage(response, document.body?.dataset?.cartUpdateError);
        throw new Error('cart remove rejected');
      }
      committed = true;
      if (form.dataset.cartSurface === 'page') {
        clearCartError(form);
        clearWriteOutcome(form);
        window.location.assign(form.action);
        return;
      }
      let responseData = null;
      try {
        responseData = await response.json();
      } catch (_error) {
        responseData = null;
      }
      const refreshed = await refreshCartAfterMutation(
        isMutationResponseObject(responseData) ? responseData : null,
        { open: true }
      );
      if (!refreshed) throw new UnknownWriteOutcomeError();
      clearCartError(form);
      clearWriteOutcome(form);
      announce(localizedMessage(document.body?.dataset?.cartUpdated));
    } catch (_error) {
      if (writeOutcomeContracts.shouldPreserveUnknown({
        committed,
        writeOutcome: form.dataset.writeOutcome
      })) {
        markWriteUnknown(form, showCartError);
      } else {
        clearWriteOutcome(form);
        showCartError(form, requestError || document.body?.dataset?.cartUpdateError);
      }
    } finally {
      finishWrite(form, setCartPending);
    }
  };
  const getHeaderSubmenu = (toggle) => {
    const header = toggle?.closest('[data-site-header]');
    const targetId = toggle?.dataset.disclosureTarget || toggle?.getAttribute('aria-controls');
    if (!header || !targetId) return null;
    const submenu = header.querySelector(`#${CSS.escape(targetId)}`);
    return submenu?.matches('[data-header-submenu]') ? submenu : null;
  };

  const setHeaderSubmenuState = (toggle, submenu, expanded) => {
    if (!toggle || !submenu) return;
    toggle.setAttribute('aria-expanded', String(expanded));
    toggle.toggleAttribute('data-expanded', expanded);
    toggle.classList.toggle('is-expanded', expanded);
    submenu.hidden = !expanded;
    submenu.toggleAttribute('data-open', expanded);
    const item = toggle.closest('.header-nav-item');
    item?.toggleAttribute('data-expanded', expanded);
    item?.classList.toggle('is-expanded', expanded);
    const label = expanded ? toggle.dataset.collapseLabel : toggle.dataset.expandLabel;
    if (label) toggle.setAttribute('aria-label', label);
  };

  const closeHeaderSubmenu = (state, restoreFocus = false) => {
    const open = state?.openSubmenu;
    if (!open) return;
    setHeaderSubmenuState(open.toggle, open.submenu, false);
    state.openSubmenu = null;
    if (restoreFocus && isVisibleFocusTarget(open.toggle)) {
      state.restoringSubmenuFocus = true;
      open.toggle.focus({ preventScroll: true });
      state.restoringSubmenuFocus = false;
    }
  };

  const closeHeaderSubmenus = (target = null) => {
    headerStates.forEach((state, header) => {
      const open = state.openSubmenu;
      if (!open) return;
      if (target && header.contains?.(target) &&
        (open.toggle.contains(target) || open.submenu.contains(target))) return;
      closeHeaderSubmenu(state);
    });
  };

  const openHeaderSubmenu = (toggle) => {
    const header = toggle?.closest('[data-site-header]');
    const submenu = getHeaderSubmenu(toggle);
    const state = header ? headerStates.get(header) : null;
    if (!state || !submenu) return;
    headerStates.forEach((otherState) => {
      if (otherState !== state) closeHeaderSubmenu(otherState);
    });
    if (state.openSubmenu && state.openSubmenu.toggle !== toggle) closeHeaderSubmenu(state);
    setHeaderSubmenuState(toggle, submenu, true);
    state.openSubmenu = { toggle, submenu, item: toggle.closest('.header-nav-item') };
  };

  const removeHeaderState = (header, state) => {
    header.removeEventListener('focusin', state.onFocusIn);
    header.removeEventListener('click', state.onSubmenuClick);
    header.removeEventListener('keydown', state.onSubmenuKeydown);
    header.removeEventListener('focusout', state.onSubmenuFocusOut);
    header.removeEventListener('pointerover', state.onSubmenuPointerOver);
    header.removeEventListener('pointerout', state.onSubmenuPointerOut);
    header.removeEventListener('pointerdown', state.onSubmenuPointerDown);
    window.removeEventListener('scroll', state.onScroll);
    closeHeaderSubmenu(state);
  };

  const syncHeaderSubmenus = (header, state) => {
    if (state.openSubmenu &&
      (!state.openSubmenu.toggle.isConnected || !state.openSubmenu.submenu.isConnected)) {
      state.openSubmenu = null;
    }
    elementsInRoot(header, '[data-header-nav-disclosure]').forEach((toggle) => {
      const submenu = getHeaderSubmenu(toggle);
      if (!submenu) return;
      const expanded = state.openSubmenu?.toggle === toggle && state.openSubmenu.submenu === submenu;
      setHeaderSubmenuState(toggle, submenu, expanded);
    });
    header.setAttribute('data-submenus-ready', '');
  };

  const cleanupHeaderBehavior = (root = null) => {
    headerStates.forEach((state, header) => {
      if (root && root !== header && !root.contains?.(header)) return;
      removeHeaderState(header, state);
      headerStates.delete(header);
    });
  };

  const initializeHeaderBehavior = (root = document) => {
    headerStates.forEach((state, header) => {
      if (!header.isConnected) {
        removeHeaderState(header, state);
        headerStates.delete(header);
      }
    });
    elementsInRoot(root, '[data-site-header]').forEach((header) => {
      const existing = headerStates.get(header);
      if (existing) {
        syncHeaderSubmenus(header, existing);
        setHeaderVisibility(header, header.classList.contains('is-hidden'));
        return;
      }
      let lastY = window.scrollY;
      let ticking = false;
      const update = () => {
        const currentY = window.scrollY;
        header.classList.toggle('is-scrolled', currentY > 40);
        const focusInsideHeader = header.contains(document.activeElement);
        if (header.hasAttribute('data-persistent-header')) {
          setHeaderVisibility(header, false);
        } else if (!openOverlayElement && !focusInsideHeader && currentY > 140 && currentY > lastY + 4) {
          setHeaderVisibility(header, true);
        } else if (focusInsideHeader || currentY < lastY - 4 || currentY <= 140 || openOverlayElement) {
          setHeaderVisibility(header, false);
        }
        lastY = currentY;
        ticking = false;
      };
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      };
      const onFocusIn = (event) => {
        setHeaderVisibility(header, false);
        if (state.restoringSubmenuFocus) return;
        const toggle = event.target.closest?.('[data-header-nav-disclosure]');
        if (toggle) openHeaderSubmenu(toggle);
      };
      const state = {
        onScroll,
        onFocusIn,
        onSubmenuClick: null,
        onSubmenuKeydown: null,
        onSubmenuFocusOut: null,
        onSubmenuPointerOver: null,
        onSubmenuPointerOut: null,
        onSubmenuPointerDown: null,
        openSubmenu: null,
        touchToggle: null,
        touchWasOpen: false,
        restoringSubmenuFocus: false
      };
      state.onSubmenuClick = (event) => {
        const toggle = event.target.closest?.('[data-header-nav-disclosure]');
        if (!toggle || !header.contains(toggle)) return;
        // Preserve normal link navigation; the first touch exposes its categories.
        if ((event.pointerType === 'touch' || state.touchToggle === toggle) && !state.touchWasOpen) {
          event.preventDefault();
          openHeaderSubmenu(toggle);
        }
        state.touchToggle = null;
      };
      state.onSubmenuPointerDown = (event) => {
        const toggle = event.target.closest?.('[data-header-nav-disclosure]');
        state.touchToggle = event.pointerType === 'touch' ? toggle : null;
        state.touchWasOpen = Boolean(toggle && state.openSubmenu?.toggle === toggle);
      };
      state.onSubmenuPointerOver = (event) => {
        if (event.pointerType === 'touch') return;
        const item = event.target.closest?.('.header-nav-item');
        if (!item || item.contains(event.relatedTarget)) return;
        // Moving the pointer must not hide the category link currently in keyboard focus.
        if (state.openSubmenu?.item.contains(document.activeElement) && state.openSubmenu.item !== item) return;
        const toggle = item.querySelector('[data-header-nav-disclosure]');
        if (toggle) openHeaderSubmenu(toggle);
        else closeHeaderSubmenu(state);
      };
      state.onSubmenuPointerOut = (event) => {
        if (event.pointerType === 'touch' || !state.openSubmenu) return;
        const { item } = state.openSubmenu;
        if (!item?.contains(event.target) || item.contains(event.relatedTarget) || item.contains(document.activeElement)) return;
        closeHeaderSubmenu(state);
      };
      state.onSubmenuKeydown = (event) => {
        const toggle = event.target.closest?.('[data-header-nav-disclosure]');
        if (toggle && ['ArrowDown', 'ArrowUp', ' '].includes(event.key)) {
          event.preventDefault();
          if (event.key === ' ' && state.openSubmenu?.toggle === toggle) {
            closeHeaderSubmenu(state);
            return;
          }
          openHeaderSubmenu(toggle);
          if (event.key !== ' ') {
            const links = [...getHeaderSubmenu(toggle).querySelectorAll('a[href]')];
            const link = event.key === 'ArrowUp' ? links.at(-1) : links[0];
            link?.focus({ preventScroll: true });
          }
          return;
        }
        if (event.key !== 'Escape' || !state.openSubmenu) return;
        const active = document.activeElement;
        const { item } = state.openSubmenu;
        if (!item?.contains(event.target) && !item?.contains(active)) return;
        event.preventDefault();
        closeHeaderSubmenu(state, true);
      };
      state.onSubmenuFocusOut = (event) => {
        if (!state.openSubmenu) return;
        const item = state.openSubmenu.item;
        if (item?.contains(event.relatedTarget)) return;
        closeHeaderSubmenu(state);
      };
      headerStates.set(header, state);
      header.addEventListener('focusin', onFocusIn);
      header.addEventListener('click', state.onSubmenuClick);
      header.addEventListener('keydown', state.onSubmenuKeydown);
      header.addEventListener('focusout', state.onSubmenuFocusOut);
      header.addEventListener('pointerover', state.onSubmenuPointerOver);
      header.addEventListener('pointerout', state.onSubmenuPointerOut);
      header.addEventListener('pointerdown', state.onSubmenuPointerDown);
      window.addEventListener('scroll', onScroll, { passive: true });
      syncHeaderSubmenus(header, state);
      update();
    });
  };


  const setCollectionFiltersVisible = (collection, state, visible) => {
    if (!visible && state.tools.contains(document.activeElement)) {
      state.button.focus({ preventScroll: true });
    }
    collection.classList.toggle('collection-page--filters-hidden', !visible);
    state.tools.hidden = !visible;
    state.button.setAttribute('aria-expanded', String(visible));
    state.label.textContent = visible
      ? state.button.dataset.filterHideLabel
      : state.button.dataset.filterShowLabel;
  };

  const cleanupCollectionFilters = (root = null) => {
    collectionFilterStates.forEach((state, collection) => {
      if (root && root !== collection && !root.contains?.(collection)) return;
      state.media.removeEventListener('change', state.onChange);
      collectionFilterStates.delete(collection);
    });
  };

  const initializeCollectionFilters = (root = document) => {
    collectionFilterStates.forEach((state, collection) => {
      if (collection.isConnected) return;
      state.media.removeEventListener('change', state.onChange);
      collectionFilterStates.delete(collection);
    });
    elementsInRoot(root, '[data-collection-tools]').forEach((collection) => {
      if (collectionFilterStates.has(collection)) return;
      const button = collection.querySelector('[data-filter-toggle]');
      const tools = collection.querySelector('.collection-tools');
      const label = button?.querySelector('[data-filter-toggle-label]');
      if (!button || !tools || !label) return;
      const media = window.matchMedia('(max-width: 989px)');
      const state = {
        button,
        tools,
        label,
        media,
        // Remember each layout's choice when the viewport crosses the breakpoint.
        visibility: { compact: Number(collection.dataset.activeFilters) > 0, wide: true },
        onChange: null
      };
      state.onChange = () => setCollectionFiltersVisible(
        collection,
        state,
        state.visibility[media.matches ? 'compact' : 'wide']
      );
      collectionFilterStates.set(collection, state);
      media.addEventListener('change', state.onChange);
      state.onChange();
    });
  };

  const toggleCollectionFilters = (button) => {
    const collection = button.closest('[data-collection-tools]');
    if (!collection) return;
    initializeCollectionFilters(collection);
    const state = collectionFilterStates.get(collection);
    if (!state) return;
    const visible = state.tools.hidden;
    state.visibility[state.media.matches ? 'compact' : 'wide'] = visible;
    setCollectionFiltersVisible(collection, state, visible);
  };

  const cancelAutoFilterSubmit = (root = null) => {
    if (root && root !== pendingAutoFilterForm && !root.contains?.(pendingAutoFilterForm)) return;
    window.clearTimeout(autoFilterTimer);
    autoFilterTimer = null;
    pendingAutoFilterForm = null;
  };

  const initializeAutomaticFilters = (root = document) => {
    elementsInRoot(root, '[data-auto-filter-note]').forEach((note) => { note.hidden = false; });
  };

  const submitAutomaticFilters = (form, delay = 0) => {
    cancelAutoFilterSubmit();
    if (!form?.matches('[data-auto-filter-form]')) return;
    pendingAutoFilterForm = form;
    const submit = () => {
      cancelAutoFilterSubmit();
      if (form.isConnected && form.checkValidity()) form.requestSubmit();
    };
    // Let customers finish typing either price bound before changing the page.
    if (delay) autoFilterTimer = window.setTimeout(submit, delay);
    else submit();
  };

  const automaticFilterUrl = (form) => {
    const url = new URL(form.action, window.location.href);
    url.search = '';
    // FormData includes the collection sort control outside the sidebar form.
    // Appending retains multiple selected makers; omitting page resets pagination.
    for (const [name, value] of new FormData(form)) {
      if (value !== '' && name !== 'page') url.searchParams.append(name, value);
    }
    return url;
  };


  const changeCardImage = (button, direction) => {
    const card = button.closest('[data-product-card]');
    if (!card) return;
    const images = [...card.querySelectorAll('[data-card-image]')];
    if (images.length < 2) return;
    const current = images.findIndex((image) => !image.hidden);
    const next = (current + direction + images.length) % images.length;
    images.forEach((image, index) => {
      const active = index === next;
      image.hidden = !active;
      if (active) image.setAttribute('aria-current', 'true');
      else image.removeAttribute('aria-current');
    });
    const status = card.querySelector('[data-card-image-status]');
    if (status) status.textContent = images[next].getAttribute('aria-label') || '';
  };
  const makerSearchTokens = (value) => [...String(value).matchAll(/[\p{L}\p{M}\p{N}]+/gu)].map((match) => ({
    word: match[0].normalize('NFD').replace(/\p{M}/gu, '').toLowerCase(),
    start: match.index,
    end: match.index + match[0].length
  }));

  const findPotteryMaker = (query, vendors) => {
    const tokens = makerSearchTokens(query);
    const aliases = {
      'royal doulton': ['doulton'],
      'royal copenhagen': ['copenhagen', 'copanhagen', 'royal copanhagen'],
      lladro: ['ladro', 'ladrow', 'lladrow'],
      nao: ['n a o']
    };
    const matches = [];
    vendors.forEach((vendor) => {
      if (typeof vendor !== 'string') return;
      const normalized = makerSearchTokens(vendor).map((token) => token.word).join(' ');
      if (!normalized || ['jgt', 'my store', 'unidentified maker'].includes(normalized)) return;
      let bestMatch = null;
      [normalized, ...(aliases[normalized] || [])].forEach((alias) => {
        const words = alias.split(' ');
        tokens.forEach((token, index) => {
          if (!words.every((word, offset) => tokens[index + offset]?.word === word)) return;
          if (!bestMatch || words.length > bestMatch.length) {
            bestMatch = { start: token.start, end: tokens[index + words.length - 1].end, length: words.length };
          }
        });
      });
      if (bestMatch) matches.push({ vendor, ...bestMatch });
    });
    // Multiple makers are ambiguous; keep the customer's ordinary search intact.
    if (matches.length !== 1) return null;
    const match = matches[0];
    const remainder = `${query.slice(0, match.start)} ${query.slice(match.end)}`.trim();
    return { vendor: match.vendor, remainder: makerSearchTokens(remainder).length ? remainder : '' };
  };

  const getPotteryMakerSearchUrl = (form) => {
    const potteryUrl = document.body.dataset.potterySearchUrl;
    if (!form.hasAttribute('data-maker-search') || !potteryUrl) return null;
    let vendors;
    try {
      vendors = JSON.parse(document.body.dataset.potterySearchMakers || '[]');
    } catch {
      return null;
    }
    if (!Array.isArray(vendors)) return null;
    const data = new FormData(form);
    const query = String(data.get('q') || '').trim();
    const match = findPotteryMaker(query, vendors);
    if (!match) return null;
    const url = new URL(match.remainder ? form.action : potteryUrl, window.location.origin);
    if (match.remainder) {
      url.searchParams.set('q', match.remainder);
      url.searchParams.set('type', 'product');
    }
    url.searchParams.set('filter.p.vendor', match.vendor);
    // A new maker replaces the old maker and unrelated category filters.
    const current = new URL(window.location.href);
    ['filter.v.price.gte', 'filter.v.price.lte', 'sort_by'].forEach((name) => {
      const value = data.get(name) || current.searchParams.get(name);
      if (value && !(name === 'sort_by' && value === 'relevance' && !match.remainder)) {
        url.searchParams.set(name, value);
      }
    });
    return url;
  };

  const cleanupPredictiveSearch = (root = null) => {
    predictiveSearchStates.forEach((state, form) => {
      if (root && root !== form && !root.contains?.(form)) return;
      state.input.removeEventListener('input', state.onInput);
      state.controller?.abort();
      window.clearTimeout(state.timer);
      state.requestGeneration += 1;
      predictiveSearchStates.delete(form);
    });
  };

  const predictiveResultMessage = (form, count) => {
    const template = count === 1 ? form.dataset.resultsOneMessage : form.dataset.resultsOtherMessage;
    return (template || `${count} result${count === 1 ? '' : 's'}.`)
      .replace(/\[count\]|\{\{\s*count\s*\}\}|%count%/gi, String(count));
  };

  const isCurrentPredictiveRequest = (form, state, generation) =>
    predictiveSearchStates.get(form) === state &&
    form.isConnected &&
    state.requestGeneration === generation;

  const initializePredictiveSearch = (root = document) => {
    predictiveSearchStates.forEach((state, form) => {
      if (form.isConnected) return;
      state.input.removeEventListener('input', state.onInput);
      state.controller?.abort();
      window.clearTimeout(state.timer);
      predictiveSearchStates.delete(form);
    });

    elementsInRoot(root, '[data-search-overlay-form]').forEach((form) => {
      const input = form.querySelector('[data-search-overlay-input]');
      const results = form.closest('[data-overlay]')?.querySelector('[data-predictive-results]') ||
        root.querySelector?.('[data-predictive-results]');
      const shortcuts = form.closest('[data-overlay]')?.querySelector('[data-search-shortcuts]') ||
        root.querySelector?.('[data-search-shortcuts]');
      const status = form.closest('[data-overlay]')?.querySelector('[data-predictive-status]') ||
        root.querySelector?.('[data-predictive-status]');
      if (!form.dataset.predictiveUrl || !input || !results || !shortcuts || !status ||
        predictiveSearchStates.has(form)) return;

      const lists = {
        products: results.querySelector('[data-predictive-products]'),
        collections: results.querySelector('[data-predictive-collections]'),
        info: results.querySelector('[data-predictive-info]')
      };
      if (Object.values(lists).some((list) => !list)) return;
      const state = {
        form,
        input,
        timer: null,
        controller: null,
        requestGeneration: 0,
        onInput: null
      };

      const renderList = (list, items) => {
        list.replaceChildren();
        items.forEach((item) => {
          const link = document.createElement('a');
          link.href = item.url;
          link.textContent = item.title;
          const row = document.createElement('li');
          row.append(link);
          list.append(row);
        });
      };

      const reset = () => {
        state.controller?.abort();
        state.requestGeneration += 1;
        window.clearTimeout(state.timer);
        Object.values(lists).forEach((list) => list.replaceChildren());
        results.hidden = true;
        shortcuts.hidden = false;
        status.hidden = true;
        status.textContent = '';
      };

      const onInput = () => {
        const query = input.value.trim();
        const allResults = form.closest('[data-overlay]')?.querySelector('[data-search-all]') ||
          document.querySelector('[data-search-all]');
        if (allResults) {
          const makerUrl = getPotteryMakerSearchUrl(form);
          const url = makerUrl || new URL(form.action, window.location.origin);
          if (query && !makerUrl) url.searchParams.set('q', query);
          allResults.href = url.toString();
        }
        reset();
        if (query.length < 2) return;
        shortcuts.hidden = true;
        status.hidden = false;
        status.textContent = form.dataset.loadingMessage;
        const generation = state.requestGeneration;
        state.timer = window.setTimeout(async () => {
          state.controller = new AbortController();
          try {
            const url = new URL(form.dataset.predictiveUrl, window.location.origin);
            if (!url.pathname.endsWith('.json')) url.pathname = `${url.pathname}.json`;
            url.searchParams.set('q', query);
            url.searchParams.set('resources[type]', 'product,collection,page,article');
            url.searchParams.set('resources[limit]', '6');
            const response = await fetchReadOnly(url.toString(), {
              signal: state.controller.signal,
              headers: { Accept: 'application/json' }
            });
            if (!response.ok) throw new Error(localizedMessage(form.dataset.errorMessage));
            const payload = await response.json();
            const found = payload && typeof payload === 'object' ? payload.resources?.results : null;
            if (!found || typeof found !== 'object' || Array.isArray(found)) {
              throw new Error(localizedMessage(form.dataset.errorMessage));
            }
            const products = Array.isArray(found.products) ? found.products : [];
            const collections = Array.isArray(found.collections) ? found.collections : [];
            const info = [
              ...(Array.isArray(found.pages) ? found.pages : []),
              ...(Array.isArray(found.articles) ? found.articles : [])
            ];
            if (!isCurrentPredictiveRequest(form, state, generation)) return;
            renderList(lists.products, products);
            renderList(lists.collections, collections);
            renderList(lists.info, info);
            const count = products.length + collections.length + info.length;
            results.hidden = count === 0;
            status.hidden = false;
            status.textContent = count === 0
              ? localizedMessage(form.dataset.noResultsMessage)
              : predictiveResultMessage(form, count);
          } catch (error) {
            if (error.name === 'AbortError' || !isCurrentPredictiveRequest(form, state, generation)) return;
            results.hidden = true;
            status.hidden = false;
            status.textContent = localizedMessage(form.dataset.errorMessage);
          } finally {
            if (isCurrentPredictiveRequest(form, state, generation)) state.controller = null;
          }
        }, 180);
      };

      state.onInput = onInput;
      predictiveSearchStates.set(form, state);
      input.addEventListener('input', onInput);
    });
  };
  const recommendationErrorMessage = (section) =>
    localizedMessage(
      section?.dataset?.errorMessage,
      section?.querySelector('[data-recommendation-error]')?.textContent
    );

  const isCurrentRecommendationRequest = (section, state, generation) =>
    recommendationStates.get(section) === state &&
    section.isConnected &&
    state.content?.isConnected &&
    section.contains(state.content) &&
    state.generation === generation;

  const showRecommendationError = (state, message) => {
    const { section, content } = state;
    if (!section.isConnected) return;
    section.dataset.recommendationState = 'error';
    section.hidden = false;
    section.setAttribute('aria-busy', 'false');
    section.querySelector('[data-recommendation-loading]')?.setAttribute('hidden', '');
    const error = section.querySelector('[data-recommendation-error]');
    const retry = state.retry || section.querySelector('[data-recommendation-retry]');
    const visibleMessage = localizedMessage(message, recommendationErrorMessage(section));
    if (error) {
      error.textContent = visibleMessage;
      error.hidden = false;
    } else {
      content.replaceChildren(document.createTextNode(visibleMessage));
    }
    if (retry) retry.hidden = false;
  };

  const getRecommendationObserver = () => {
    if (recommendationObserverInitialized) return recommendationObserver;
    recommendationObserverInitialized = true;
    if (typeof window.IntersectionObserver !== 'function') return null;
    recommendationObserver = new window.IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        recommendationObserver?.unobserve(entry.target);
        const state = recommendationStates.get(entry.target);
        if (!state || state.started || !entry.target.isConnected) return;
        void hydrateRecommendations(state);
      });
    }, { rootMargin: '300px 0px', threshold: 0 });
    return recommendationObserver;
  };

  const hydrateRecommendations = async (state) => {
    if (!state || state.started) return;
    const { section } = state;
    const retry = state.retry || section.querySelector('[data-recommendation-retry]');
    const errorElement = section.querySelector('[data-recommendation-error]');
    const loading = section.querySelector('[data-recommendation-loading]');
    state.started = true;
    state.controller?.abort();
    const generation = state.generation + 1;
    state.generation = generation;
    const controller = new AbortController();
    state.controller = controller;
    section.hidden = false;
    section.setAttribute('aria-busy', 'true');
    if (retry) retry.hidden = true;
    if (errorElement) errorElement.hidden = true;
    if (loading) loading.hidden = false;

    try {
      const url = new URL(section.dataset.recommendationUrl, window.location.origin);
      url.searchParams.set('section_id', section.dataset.recommendationSectionId);
      url.searchParams.set('product_id', section.dataset.recommendationProductId);
      url.searchParams.set('limit', section.dataset.recommendationLimit || '4');
      url.searchParams.set('intent', section.dataset.recommendationIntent);
      const response = await fetchReadOnly(url.toString(), {
        signal: controller.signal,
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!isCurrentRecommendationRequest(section, state, generation) || controller.signal.aborted) return;
      if (!response.ok) throw new Error(recommendationErrorMessage(section));
      const html = await response.text();
      if (!isCurrentRecommendationRequest(section, state, generation)) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      if (!isCurrentRecommendationRequest(section, state, generation)) return;
      const roots = [...parsed.querySelectorAll('[data-product-recommendations]')];
      if (roots.length !== 1) throw new Error(recommendationErrorMessage(section));
      const replacement = roots[0];
      if (
        replacement.dataset.recommendationSectionId !== section.dataset.recommendationSectionId ||
        replacement.dataset.recommendationProductId !== section.dataset.recommendationProductId ||
        replacement.dataset.recommendationIntent !== section.dataset.recommendationIntent
      ) throw new Error(recommendationErrorMessage(section));
      const contentWrappers = [...replacement.querySelectorAll('[data-recommendation-content]')];
      if (contentWrappers.length !== 1) throw new Error(recommendationErrorMessage(section));
      const replacementContent = contentWrappers[0];
      const lists = [...replacementContent.querySelectorAll('[data-recommendation-list]')];
      const emptyMarkers = [...replacementContent.querySelectorAll('[data-recommendation-empty]')];
      const list = lists.length === 1 ? lists[0] : null;
      const hasItems = Boolean(
        list &&
        list.children.length > 0 &&
        list.querySelector('[data-product-card]')
      );
      if (!recommendationContracts.isValidContent({
        contentCount: contentWrappers.length,
        hasList: lists.length === 1,
        hasItems,
        hasEmptyMarker: emptyMarkers.length === 1
      })) throw new Error(recommendationErrorMessage(section));
      if (!isCurrentRecommendationRequest(section, state, generation)) return;
      state.content.replaceChildren(...replacementContent.childNodes);
      initializeRandomProductGrids(state.content);
      section.dataset.recommendationState = hasItems ? 'ready' : 'empty';
      section.hidden = !hasItems;
      section.setAttribute('aria-busy', 'false');
      if (retry) retry.hidden = true;
    } catch (error) {
      if (error.name === 'AbortError' || !isCurrentRecommendationRequest(section, state, generation)) return;
      showRecommendationError(state, recommendationErrorMessage(section));
    } finally {
      if (isCurrentRecommendationRequest(section, state, generation)) state.controller = null;
    }
  };

  const cleanupRecommendations = (root = null) => {
    recommendationStates.forEach((state, section) => {
      if (root && root !== section && !root.contains?.(section)) return;
      recommendationObserver?.unobserve(section);
      state.retry?.removeEventListener('click', state.onRetry);
      state.controller?.abort();
      state.generation += 1;
      recommendationStates.delete(section);
    });
  };

  const initializeRecommendations = (root = document) => {
    recommendationStates.forEach((state, section) => {
      if (section.isConnected) return;
      recommendationObserver?.unobserve(section);
      state.retry?.removeEventListener('click', state.onRetry);
      state.controller?.abort();
      state.generation += 1;
      recommendationStates.delete(section);
    });
    const observer = window.Shopify?.designMode ? null : getRecommendationObserver();
    const loadImmediately = Boolean(window.Shopify?.designMode) || !observer;
    elementsInRoot(root, '[data-product-recommendations]').forEach((section) => {
      if (recommendationStates.has(section)) return;
      if (section.dataset.recommendationState === 'empty') {
        section.hidden = true;
        return;
      }
      const content = section.querySelector('[data-recommendation-content]');
      if (
        !content ||
        !section.dataset.recommendationUrl ||
        !section.dataset.recommendationSectionId ||
        !section.dataset.recommendationProductId ||
        !section.dataset.recommendationIntent
      ) return;
      const retry = section.querySelector('[data-recommendation-retry]');
      const state = {
        section,
        content,
        retry,
        controller: null,
        generation: 0,
        started: false,
        onRetry: null
      };
      if (retry) {
        state.onRetry = (event) => {
          event.preventDefault();
          if (!recommendationContracts.canRetry({
            connected: section.isConnected,
            state: section.dataset.recommendationState,
            started: state.started,
            retryVisible: !retry.hidden
          })) return;
          state.controller?.abort();
          state.generation += 1;
          state.started = false;
          void hydrateRecommendations(state);
        };
        retry.addEventListener('click', state.onRetry);
      }
      recommendationStates.set(section, state);
      if (loadImmediately) {
        void hydrateRecommendations(state);
      } else {
        observer.observe(section);
      }
    });
  };


  const enableJavaScriptProductControls = (form) => {
    const optionControls = form.querySelector('[data-product-option-controls]');
    if (optionControls) {
      optionControls.hidden = false;
      optionControls.removeAttribute('aria-hidden');
    }
    const sellingPlanSelector = form.querySelector('[data-selling-plan-selector]');
    if (sellingPlanSelector) sellingPlanSelector.hidden = false;
    const noJsSellingPlan = form.querySelector('[data-no-js-selling-plan]');
    if (noJsSellingPlan) noJsSellingPlan.disabled = true;
    const sellingPlanInput = form.querySelector('[data-selling-plan-input]');
    if (sellingPlanInput && getCurrentVariantInput(form)?.value) {
      sellingPlanInput.name = 'selling_plan';
      sellingPlanInput.disabled = false;
    }
  };
  const updateGiftCardRecipient = (recipient) => {
    const toggle = recipient.querySelector('[data-gift-card-recipient-toggle]');
    const enabled = toggle?.checked === true;
    const propertyFields = [...recipient.querySelectorAll('[name^="properties["]')]
      .filter((field) => field !== toggle);
    propertyFields.forEach((field) => {
      field.disabled = !enabled;
      if (!enabled) field.value = '';
    });
    const email = recipient.querySelector('[data-gift-card-recipient-email]');
    if (email) {
      email.required = enabled;
      email.setAttribute('aria-required', String(enabled));
    }
    const offset = recipient.querySelector('[data-gift-card-recipient-offset]');
    if (offset) {
      offset.disabled = !enabled;
      offset.value = enabled ? String(new Date().getTimezoneOffset()) : '';
    }
  };

  const initializeGiftCardRecipients = (root = document) => {
    elementsInRoot(root, '[data-gift-card-recipient]').forEach(updateGiftCardRecipient);
  };


  const initializeProductForms = (root = document) => {
    elementsInRoot(root, '[data-product-form]').forEach((form) => {
      enableJavaScriptProductControls(form);
      initializeCurrentQuantity(form);
      syncCurrentSellingPlan(form);
      updateCurrentPurchaseState(form);
    });
    initializeGiftCardRecipients(root);
  };



  const productSelection = (() => {
    const states = new WeakMap();

    const getState = (root) => {
      let state = states.get(root);
      if (!state) {
        state = { generation: 0, controller: null };
        states.set(root, state);
      }
      return state;
    };

    const isCurrentRequest = (root, state, generation) =>
      productSelectionContracts.isCurrentRequest({
        sameState: states.get(root) === state,
        currentGeneration: state.generation,
        generation,
        connected: root.isConnected
      });

    const collectSelectedOptions = (root) => {
      const groups = [...root.querySelectorAll('[data-product-option]')].map((group) => ({
        position: group.dataset.optionPosition,
        selection: (() => {
          const control = group.querySelector('[data-product-option-value]:checked');
          if (!control?.dataset.optionValueId) return null;
          return {
            id: control.dataset.optionValueId,
            productUrl: control.dataset.productUrl || '',
            control
          };
        })()
      }));
      return productSelectionContracts.orderOptionSelections(groups);
    };

    const restoreOptionSelection = (root, optionIds) => {
      const wanted = new Set(optionIds || []);
      root.querySelectorAll('[data-product-option]').forEach((group) => {
        group.querySelectorAll('[data-product-option-value]').forEach((control) => {
          control.checked = wanted.has(control.dataset.optionValueId);
        });
      });
    };

    const captureFocus = (root) => {
      const active = document.activeElement;
      if (!active || !root.contains(active)) return null;
      const descriptor = focusDescriptor(active, root);
      if (!descriptor) return null;
      return {
        ...descriptor,
        optionValueId: active.dataset?.optionValueId || '',
        optionPosition: active.dataset?.optionPosition || '',
        optionName: active.dataset?.optionName || ''
      };
    };

    const normalizeOptionIds = (optionIds) => (optionIds || [])
      .map((entry) => typeof entry === 'string' ? entry : entry?.id)
      .filter(Boolean);

    const captureFormState = (root, optionIds) => {
      const form = elementInRoot(root, '[data-product-form]');
      const quantity = form?.querySelector('input[name="quantity"]');
      const giftFields = [];
      form?.querySelectorAll('[data-gift-card-recipient] [name]').forEach((field) => {
        giftFields.push({
          name: field.name,
          value: field.value,
          checked: 'checked' in field ? field.checked : false
        });
      });
      return {
        optionIds: normalizeOptionIds(optionIds),
        focus: captureFocus(root),
        quantity: quantity?.value || '',
        giftFields
      };
    };

    const restoreFormState = (root, previous) => {
      const form = elementInRoot(root, '[data-product-form]');
      if (!form) return;
      const quantity = form.querySelector('input[name="quantity"]');
      if (quantity && previous.quantity !== '') normalizeQuantityInput(quantity, previous.quantity);
      const recipient = form.querySelector('[data-gift-card-recipient]');
      if (!recipient) return;
      previous.giftFields.forEach((saved) => {
        const field = [...recipient.querySelectorAll('[name]')].find(({ name }) => name === saved.name);
        if (!field) return;
        if ('checked' in field) field.checked = saved.checked;
        else field.value = saved.value;
      });
    };

    const findFocusTarget = (root, descriptor) => {
      if (!descriptor) return null;
      const controls = [...root.querySelectorAll('[data-product-option-value]')];
      const candidate = productSelectionContracts.findFocusCandidate(
        controls.map((control) => ({
          element: control,
          optionValueId: control.dataset.optionValueId,
          optionPosition: control.dataset.optionPosition,
          optionName: control.dataset.optionName
        })),
        descriptor
      );
      return candidate || findFocusEquivalent(root, descriptor);
    };

    const focusReplacement = (root, descriptor) => {
      const target = findFocusTarget(root, descriptor);
      if (target && !target.disabled) {
        target.focus({ preventScroll: true });
        return;
      }
      const fallback = productSelectionContracts.findFocusFallback(
        [...root.querySelectorAll('[data-product-error], [data-product-option-value], [data-add-to-cart]')].map((element) => ({
          element,
          disabled: element.disabled,
          exposed: isExposed(element)
        }))
      );
      if (fallback) fallback.focus({ preventScroll: true });
    };

    const updateHistory = (root, optionIds) => {
      const selectedSellingPlan = root.querySelector('[data-selling-plan-input]')?.value || '';
      const nextHref = productSelectionContracts.buildHistoryHref({
        currentUrl: window.location.href,
        productUrl: root.dataset.productUrl,
        variantUrl: root.dataset.currentVariantUrl,
        variantId: root.dataset.currentVariantId,
        optionIds: root.dataset.selectedOptionIds
          ? root.dataset.selectedOptionIds.split(',').filter(Boolean)
          : optionIds,
        selectedSellingPlan,
        updateUrl: root.dataset.updateUrl === 'true'
      });
      if (nextHref) window.history.replaceState({}, '', nextHref);
    };

    const errorMessage = (root) =>
      localizedMessage(
        root?.dataset?.updateError,
        document.body?.dataset?.productUpdateError
      );

    const setPending = (root, pending) => {
      root.dataset.pending = pending ? 'true' : 'false';
      root.setAttribute('aria-busy', pending ? 'true' : 'false');
      const form = elementInRoot(root, '[data-product-form]');
      if (form) setProductPending(form, pending);
    };

    const initialize = (root = document) => {
      elementsInRoot(root, '[data-product-section]').forEach((section) => {
        getState(section);
        initializeProductForms(section);
      });
    };

    const unload = (root = document) => {
      elementsInRoot(root, '[data-product-section]').forEach((section) => {
        const state = states.get(section);
        if (!state) return;
        state.generation += 1;
        state.controller?.abort();
        state.controller = null;
        setPending(section, false);
        states.delete(section);
      });
    };

    const requestReplacement = async (control) => {
      const root = control?.closest('[data-product-section]');
      if (!root || !root.isConnected || control.disabled) return false;
      let state = states.get(root);
      if (!state) {
        initialize(root);
        state = states.get(root);
      }
      const selected = collectSelectedOptions(root);
      if (!selected) return false;
      const targetProductUrl = control.dataset.productUrl || root.dataset.productUrl;
      const form = elementInRoot(root, '[data-product-form]');
      const sellingPlanInput = form?.querySelector('[data-selling-plan-input]');
      const sellingPlan = sellingPlanInput && !sellingPlanInput.disabled ? sellingPlanInput.value : undefined;
      if (root.dataset.optionChangeNavigation === 'full-page') {
        const navigationHref = productSelectionContracts.buildNavigationHref({
          targetProductUrl,
          pageUrl: window.location.href,
          optionIds: selected.map(({ id }) => id),
          sellingPlan
        });
        if (!navigationHref) {
          showProductError(form, errorMessage(root));
          return false;
        }
        window.location.assign(navigationHref);
        return true;
      }
      const previousOptionIds = normalizeOptionIds((root.dataset.selectedOptionIds || '').split(',').filter(Boolean));
      const previous = captureFormState(root, previousOptionIds);
      const generation = productSelectionContracts.beginRequest(state);
      const controller = new AbortController();
      state.controller = controller;
      clearProductError(form);
      setPending(root, true);
      try {
        const requestUrl = productSelectionContracts.buildRequestUrl({
          targetProductUrl,
          pageUrl: window.location.href,
          sectionId: root.dataset.sectionId,
          optionIds: selected.map(({ id }) => id),
          sellingPlan
        });
        if (!requestUrl) throw new Error(errorMessage(root));
        const response = await fetchReadOnly(requestUrl.toString(), {
          headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
          signal: controller.signal
        });
        if (!response.ok) throw new Error(errorMessage(root));
        const html = await response.text();
        if (!isCurrentRequest(root, state, generation)) return false;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const roots = [...parsed.querySelectorAll('[data-product-section]')];
        if (roots.length !== 1) throw new Error(errorMessage(root));
        const replacement = roots[0];
        const replacementForm = replacement.querySelector('[data-product-form]');
        const replacementVariantInput = replacementForm?.querySelector('[data-variant-id-input]');
        const replacementAddButton = replacementForm?.querySelector('[data-add-to-cart]');
        const replacementQuantity = replacementForm?.querySelector('input[name="quantity"]');
        const expectedOptionGroups = [...root.querySelectorAll('[data-product-option]')];
        const optionGroups = [...replacement.querySelectorAll('[data-product-option]')];
        const expectedProductPath = productSelectionContracts.normalizeProductPath(root.dataset.productUrl, window.location.href);
        const targetProductPath = productSelectionContracts.normalizeProductPath(targetProductUrl, window.location.href);
        const sameProduct = Boolean(expectedProductPath && targetProductPath && expectedProductPath === targetProductPath);
        const declaredGroupCount = parseInteger(replacement.dataset.optionGroupCount);
        const buildOptionGroupDetails = (groups) => groups.map((group) => {
          const controls = [...group.querySelectorAll('[data-product-option-value]')];
          const position = group.dataset.optionPosition || '';
          const name = group.dataset.optionName || '';
          return {
            controlCount: controls.length,
            selectedCount: controls.filter((control) => control.checked).length,
            valueCount: parseInteger(group.dataset.optionValueCount),
            position,
            name,
            controlIds: controls.map((control) => control.dataset.optionValueId || ''),
            controlsValid: Boolean(
              position &&
              name &&
              controls.every((control) =>
                typeof control.dataset.optionValueId === 'string' &&
                control.dataset.optionValueId.trim() !== '' &&
                control.dataset.optionPosition === position &&
                control.dataset.optionName === name
              )
            )
          };
        });
        const expectedGroupDetails = buildOptionGroupDetails(expectedOptionGroups);
        const optionGroupDetails = buildOptionGroupDetails(optionGroups);
        const hasOptionGroupCount = declaredGroupCount !== null &&
          optionGroups.length === declaredGroupCount &&
          (!sameProduct || expectedOptionGroups.length === declaredGroupCount);
        const isNullVariant = replacement.dataset.currentVariantId === '';
        const hasOptionSelections = productSelectionContracts.isValidOptionGroups({
          expectedCount: declaredGroupCount,
          groups: optionGroupDetails,
          isNullVariant,
          expectedGroups: expectedGroupDetails,
          requireCompatibleIdentity: sameProduct
        });
        const hasSellingPlanInput = !replacement.querySelector('[data-selling-plan-options]') ||
          Boolean(replacementForm?.querySelector('[data-selling-plan-input]'));
        const paymentButtons = [...replacement.querySelectorAll('.shopify-payment-button')];
        const hasNullVariantState = isNullVariant
          ? replacement.dataset.currentVariantUrl === '' &&
            replacement.dataset.currentMediaId === '' &&
            replacement.dataset.selectedOptionIds === '' &&
            replacementVariantInput?.value === '' &&
            replacementVariantInput?.disabled === true &&
            replacementVariantInput?.getAttribute('name') !== 'id' &&
            replacementAddButton?.disabled === true &&
            paymentButtons.every((button) => button.inert === true || button.hasAttribute('inert'))
          : typeof replacement.dataset.currentVariantUrl === 'string' &&
            replacement.dataset.currentVariantUrl.trim() !== '';
        const hasVariantInputState = isNullVariant
          ? Boolean(
            replacementVariantInput &&
            replacementVariantInput.value === '' &&
            replacementVariantInput.disabled === true &&
            replacementVariantInput.getAttribute('name') !== 'id'
          )
          : Boolean(
            replacementVariantInput &&
            replacementVariantInput.value === replacement.dataset.currentVariantId &&
            replacementVariantInput.getAttribute('name') === 'id' &&
            (
              replacementVariantInput.disabled === false ||
              (
                replacementVariantInput.disabled === true &&
                (
                  replacementVariantInput.dataset.available === 'false' ||
                  replacementVariantInput.dataset.requiresSellingPlan === 'true'
                ) &&
                replacementAddButton?.disabled === true
              )
            )
          );
        const hasPurchaseControlState = isNullVariant
          ? Boolean(replacementAddButton?.disabled === true)
          : Boolean(
            replacementVariantInput &&
            replacementQuantity &&
            replacementQuantity.disabled === false &&
            replacementAddButton &&
            (
              replacementAddButton.disabled === false ||
              replacementVariantInput.dataset.available === 'false' ||
              replacementVariantInput.dataset.requiresSellingPlan === 'true'
            )
          );
        const hasValidMetadata = productSelectionContracts.isValidReplacement({
          expectedSectionId: root.dataset.sectionId,
          sectionId: replacement.dataset.sectionId,
          expectedProductUrl: targetProductUrl,
          productUrl: replacement.dataset.productUrl,
          pageUrl: window.location.href,
          hasProductSelection: replacement.hasAttribute('data-product-selection'),
          hasCurrentVariantId: replacement.hasAttribute('data-current-variant-id'),
          hasCurrentVariantUrl: replacement.hasAttribute('data-current-variant-url'),
          hasCurrentMediaId: replacement.hasAttribute('data-current-media-id'),
          hasSelectedOptionIds: replacement.hasAttribute('data-selected-option-ids'),
          hasUpdateUrl: replacement.hasAttribute('data-update-url'),
          hasForm: replacement.querySelectorAll('[data-product-form]').length === 1,
          hasEssentialControls: Boolean(
            replacementForm &&
            replacementVariantInput &&
            replacementAddButton &&
            replacementQuantity
          ),
          hasVariantInput: Boolean(replacementVariantInput),
          hasAddButton: Boolean(replacementAddButton),
          hasQuantityInput: Boolean(replacementQuantity),
          hasSellingPlanInput,
          hasOptionSelections,
          hasOptionGroupCount,
          hasNullVariantState,
          hasVariantInputState,
          hasPurchaseControlState
        });
        if (!hasValidMetadata) throw new Error(errorMessage(root));
        if (!isCurrentRequest(root, state, generation)) return false;
        restoreFormState(replacement, previous);
        states.delete(root);
        state.controller = null;
        root.replaceWith(replacement);
        initialize(replacement);
        focusReplacement(replacement, previous.focus);
        updateHistory(replacement, selected.map(({ id }) => id));
        return true;
      } catch (caughtError) {
        if (caughtError.name === 'AbortError' || !isCurrentRequest(root, state, generation)) return false;
        restoreOptionSelection(root, previous.optionIds);
        setPending(root, false);
        initializeProductForms(root);
        showProductError(form, errorMessage(root));
        return false;
      } finally {
        if (isCurrentRequest(root, state, generation)) {
          state.controller = null;
          setPending(root, false);
        }
      }
    };

    return {
      initialize,
      unload,
      handleOptionChange: requestReplacement,
      contracts: productSelectionContracts
    };
  })();

  window.productSelection = productSelection;

  document.addEventListener('click', async (event) => {
    if (event.target.closest('a[href]')) cancelAutoFilterSubmit();
    closeHeaderSubmenus(event.target);
    const opener = event.target.closest('[data-overlay-open]');
    if (opener) {
      event.preventDefault();
      openOverlay(opener.dataset.overlayOpen, opener);
      return;
    }

    const closer = event.target.closest('[data-overlay-close]');
    if (closer) {
      event.preventDefault();
      closeOverlay();
      return;
    }
    const filterToggle = event.target.closest('[data-filter-toggle]');
    if (filterToggle) {
      toggleCollectionFilters(filterToggle);
      return;
    }

    const previousImage = event.target.closest('[data-card-previous]');
    if (previousImage) {
      changeCardImage(previousImage, -1);
      return;
    }

    const nextImage = event.target.closest('[data-card-next]');
    if (nextImage) {
      changeCardImage(nextImage, 1);
      return;
    }

    const searchClear = event.target.closest('[data-search-clear]');
    if (searchClear) {
      window.requestAnimationFrame(() => {
        const input = searchClear.closest('form')?.querySelector('[data-search-overlay-input]');
        if (input) {
          input.value = '';
          input.focus();
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });
      return;
    }


    const removeLink = event.target.closest('[data-cart-remove]');
    if (removeLink) {
      event.preventDefault();
      if (removeLink.getAttribute('aria-disabled') === 'true') return;
      await removeCartLine(removeLink);
    }
  });

  document.addEventListener('submit', async (event) => {
    cancelAutoFilterSubmit();
    const filterForm = event.target.closest('[data-auto-filter-form]');
    if (filterForm && !event.defaultPrevented) {
      event.preventDefault();
      window.location.assign(automaticFilterUrl(filterForm).toString());
      return;
    }

    const makerSearchForm = event.target.closest('[data-maker-search]');
    if (makerSearchForm && !event.defaultPrevented) {
      const url = getPotteryMakerSearchUrl(makerSearchForm);
      if (url) {
        event.preventDefault();
        window.location.assign(url.toString());
        return;
      }
    }

    const productForm = event.target.closest('[data-product-form]');
    if (productForm) {
      event.preventDefault();
      await submitProductForm(productForm);
      return;
    }

    const cartForm = event.target.closest('[data-cart-form]');
    if (cartForm && event.submitter?.name !== 'checkout') {
      event.preventDefault();
      await submitCartForm(cartForm);
    }
  });

  document.addEventListener('input', (event) => {
    const priceInput = event.target.closest('[data-auto-filter-form] input[type="number"]');
    if (priceInput) submitAutomaticFilters(priceInput.form, 650);
  });

  document.addEventListener('change', (event) => {
    const filterControl = event.target;
    if (filterControl.form?.matches('[data-auto-filter-form]')) {
      submitAutomaticFilters(filterControl.form, filterControl.type === 'number' ? 650 : 0);
      return;
    }
    const collectionSort = event.target.closest('[data-collection-sort]');
    if (collectionSort?.form) {
      collectionSort.form.requestSubmit();
      return;
    }
    const giftCardToggle = event.target.closest('[data-gift-card-recipient-toggle]');
    if (giftCardToggle) {
      const recipient = giftCardToggle.closest('[data-gift-card-recipient]');
      if (recipient) updateGiftCardRecipient(recipient);
      return;
    }

    const optionValue = event.target.closest('[data-product-option-value]');
    if (optionValue) {
      productSelection.handleOptionChange(optionValue);
      return;
    }

    const sellingPlanOption = event.target.closest('[data-selling-plan-option]');
    if (sellingPlanOption && !sellingPlanOption.disabled) {
      const form = sellingPlanOption.closest('[data-product-form]');
      if (form) syncCurrentSellingPlan(form);
    }
  });

  const handleSectionUnload = (event) => {
    const root = event.target;
    const overlay = elementInRoot(root, '[data-overlay]');
    if (overlay && openOverlayElement &&
      (openOverlayElement === overlay || root.contains?.(openOverlayElement))) {
      closeOverlay({ restoreFocus: false });
    } else if (openOverlayElement && !openOverlayElement.isConnected) {
      clearDetachedOverlayState(openOverlayElement);
    }
    cleanupWriteWatchdogs(root);
    cancelCartDrawerRefresh(root);
    cleanupHeaderBehavior(root);
    cleanupCollectionFilters(root);
    cancelAutoFilterSubmit(root);
    cleanupPredictiveSearch(root);
    cleanupRecommendations(root);
    productSelection.unload(root);
  };

  const handleSectionLoad = (event) => {
    const root = event.target;
    elementsInRoot(root, '[data-overlay]').forEach((overlay) => {
      if (openOverlayElement !== overlay) setOverlayState(overlay, false);
    });
    initializeHeaderBehavior(root);
    initializeCollectionFilters(root);
    initializeAutomaticFilters(root);
    initializePredictiveSearch(root);
    initializeRandomProductRails(root);
    initializeRandomProductGrids(root);
    productSelection.initialize(root);
    initializeRecommendations(root);
    focusServerErrorSummary(root);
  };

  document.addEventListener('shopify:section:unload', handleSectionUnload);
  document.addEventListener('shopify:section:load', handleSectionLoad);

  window.addEventListener('pagehide', () => cancelAutoFilterSubmit());

  window.addEventListener('pageshow', (event) => {
    cancelAutoFilterSubmit();
    // History restoration can retain submitted controls even without the back/forward cache.
    elementsInRoot(document, '[data-auto-filter-form]').forEach((form) => form.reset());
    initializeRandomProductGrids(document, event.persisted);
    if (!event.persisted) return;
    elementsInRoot(document, '[data-random-product-rail]').forEach((rail) => {
      const grid = rail.querySelector('[data-product-rail-grid]');
      if (grid && grid.children.length > 1) grid.replaceChildren(...shuffledItems(grid.children));
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeHeaderSubmenus();
    if (event.key === 'Escape' && openOverlayElement) {
      event.preventDefault();
      closeOverlay();
      return;
    }
    trapFocus(event);
  });

  document.querySelectorAll('[data-overlay-open]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
  elementsInRoot(document, '[data-overlay]').forEach((overlay) => setOverlayState(overlay, false));
  const hashOverlay = window.location.hash ? document.getElementById(window.location.hash.slice(1)) : null;
  if (hashOverlay?.dataset.overlay) openOverlay(hashOverlay.dataset.overlay);
  if (window.__themeResilienceTest === true) {
    window.themeResilience = Object.freeze({
      readOnlyTimeoutMs: READ_ONLY_TIMEOUT_MS,
      writeWatchdogTimeoutMs: WRITE_WATCHDOG_TIMEOUT_MS,
      customerMessageMaxLength: CUSTOMER_MESSAGE_MAX_LENGTH,
      fetchReadOnly,
      startWriteWatchdog,
      clearWriteWatchdog,
      finishWrite,
      isBoundedCustomerMessage,
      isMutationResponseObject,
      errorMessage,
      productSelectionContracts,
      writeOutcomeContracts,
      cartMarkupContracts,
      recommendationContracts,
      refreshCartAfterMutation,
      refreshCartDrawer,
      applyBundledCartSection,
      cancelCartDrawerRefresh,
      openOverlay,
      focusDescriptor,
      findFocusEquivalent,
      submitProductForm,
      submitCartForm,
      removeCartLine
    });
  }
  initializeHeaderBehavior();
  initializeCollectionFilters();
  initializeAutomaticFilters();
  initializePredictiveSearch();
  initializeRandomProductRails();
  initializeRandomProductGrids();
  initializeRecommendations();
  productSelection.initialize();
  focusServerErrorSummary();
})();
